import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { db } from '../../firebase'
import {
  collection,
  addDoc,
  getDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  orderBy,
  query
} from 'firebase/firestore'
import ProtectedRoute from '../../components/ProtectedRoute'
import ImageUploader from '../../components/ImageUploader'
import InjuryIcon from '../../components/InjuryIcon'
import { PremiumFeaturePill } from '../../components/premium/PremiumInfoButton'
import CHAMPIONSHIPS from '../../data/championships'
import { getBrandBadgeClass } from '../../utils/brandBadge'
import {
  INTERIM_CHAMPION_STATUS,
  isInterimChampionForTitle,
  normalizeChampionStatusByTitle
} from '../../utils/championStatus'
import {
  buildChangeDescription,
  formatAdminBoolean,
  recordAdminUpdate
} from '../../utils/adminUpdates'
import {
  buildTitleChangePenaltySuggestions,
  formatSuggestedShowLabel,
  isChampionDaysInBonusWindow,
  loadTitleBonusSuggestionBundle
} from '../../utils/titleBonusSuggestions'
import '../../styles/AddWrestler.css'

const BRAND_OPTIONS = [
  { value: 'Free Agent', label: 'Free Agent'},
  { value: 'Raw', label: 'Raw'},
  { value: 'SmackDown', label: 'SmackDown'},
  { value: 'NXT', label: 'NXT' }
]

const GENDER_OPTIONS = [
  { value: 'Uomo', label: 'Uomo', icon: 'bi-gender-male' },
  { value: 'Donna', label: 'Donna', icon: 'bi-gender-female' }
]

const ALIGNMENT_OPTIONS = [
  { value: 'face', label: 'Face', icon: 'bi-emoji-smile' },
  { value: 'heel', label: 'Heel', icon: 'bi-emoji-angry-fill' }
]

const getSeasonScores = data => {
  if (Array.isArray(data.scores)) return data.scores
  if (Array.isArray(data.pastScores)) return data.pastScores
  return []
}

function AddWrestler () {
  const [name, setName] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [score, setScore] = useState('')
  const [message, setMessage] = useState('')
  const [wrestlers, setWrestlers] = useState([])
  const [editingId, setEditingId] = useState(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [brand, setBrand] = useState('Free Agent')
  const [gender, setGender] = useState('')
  const [championTypes, setChampionTypes] = useState([''])
  const [searchTerm, setSearchTerm] = useState('')
  const [isInjured, setIsInjured] = useState(false)
  const [useImageUpload, setUseImageUpload] = useState(false)
  const [sortBy, setSortBy] = useState('name')
  const [sortOrder, setSortOrder] = useState('asc')
  const [expandedEditId, setExpandedEditId] = useState(null)
  const [alignment, setAlignment] = useState('')
  const [active, setActive] = useState(true)
  const [isGuest, setIsGuest] = useState(false)
  const [activeFilter, setActiveFilter] = useState('active')
  const [premiumComment, setPremiumComment] = useState('')

  const [showTitleModal, setShowTitleModal] = useState(false)
  const [selectedTitle, setSelectedTitle] = useState(null)
  const [titleAssignmentSearch, setTitleAssignmentSearch] = useState('')
  const [selectedWrestlers, setSelectedWrestlers] = useState([]) // Per selezione multipla
  const [savedTeams, setSavedTeams] = useState([])
  const [activeTitleTeamSuggestionId, setActiveTitleTeamSuggestionId] =
    useState('')
  const [titleModalMode, setTitleModalMode] = useState('assign')
  const [assignInterimChampion, setAssignInterimChampion] = useState(false)
  const [showTitleRemovalModal, setShowTitleRemovalModal] = useState(false)
  const [titleRemovalTitle, setTitleRemovalTitle] = useState(null)
  const [titleRemovalChampions, setTitleRemovalChampions] = useState([])
  const [titleRemovalSubmittingId, setTitleRemovalSubmittingId] = useState('')
  const [, setSelectionMode] = useState('single') // 'single', 'tag_team', 'mixed', 'trios'
  const [titleFilter, setTitleFilter] = useState('all')
  const [titleGenderFilter, setTitleGenderFilter] = useState('all')
  const [titleUniverseFilter, setTitleUniverseFilter] = useState('all')

  const [showTitleDashboard, setShowTitleDashboard] = useState(false)
  const [championTitleStats, setChampionTitleStats] = useState({})
  const [championTitleBonusHistory, setChampionTitleBonusHistory] = useState({})
  const [showsSchedule, setShowsSchedule] = useState([])
  const [bulkArchiveRunning, setBulkArchiveRunning] = useState(false)
  const [bulkResetRunning, setBulkResetRunning] = useState(false)
  const [bulkArchiveProgress, setBulkArchiveProgress] = useState({
    current: 0,
    total: 0
  })

  const fetchWrestlers = useCallback(async () => {
    try {
      const q = query(collection(db, 'wrestlers'), orderBy('name'))
      const querySnapshot = await getDocs(q)
      const wrestlersData = querySnapshot.docs.map(doc => {
        const data = doc.data()
        return {
          id: doc.id,
          ...data,
          brand: data.brand || data.roster || 'Free Agent',
          prices:
            Array.isArray(data.prices) && data.prices.length
              ? data.prices
              : data.price !== undefined && data.price !== null
              ? [{ value: data.price, date: data.updatedAt || new Date() }]
              : [],
          scores: getSeasonScores(data),
          score: data.score || 0,
          premiumComment: data.premiumComment || '',
          championTypes: Array.isArray(data.championTypes)
            ? data.championTypes
            : [],
          championImages: Array.isArray(data.championImages)
            ? data.championImages
            : [],
          championStatusByTitle: normalizeChampionStatusByTitle(
            data.championStatusByTitle,
            Array.isArray(data.championTypes) ? data.championTypes : []
          ),
          isInjured: !!data.isInjured,
          alignment: data.alignment,
          active: data.active !== undefined ? data.active : true,
          // Mantieni il price esistente
          price: data.price !== undefined ? data.price : 0
        }
      })
      const teamsSnap = await getDocs(collection(db, 'teams'))
      const wrestlerNamesById = wrestlersData.reduce((acc, wrestler) => {
        acc[wrestler.id] = wrestler.name
        return acc
      }, {})
      const nextSavedTeams = teamsSnap.docs
        .map(teamDoc => {
          const teamData = teamDoc.data()
          const members = Array.isArray(teamData.members)
            ? teamData.members.filter(Boolean)
            : []

          return {
            id: teamDoc.id,
            name: teamData.name || 'Team senza nome',
            members,
            memberNames: members
              .map(memberId => wrestlerNamesById[memberId])
              .filter(Boolean)
          }
        })
        .filter(team => team.members.length > 0)
        .sort((firstTeam, secondTeam) =>
          firstTeam.name.localeCompare(secondTeam.name)
        )

      setWrestlers(wrestlersData)
      setSavedTeams(nextSavedTeams)
    } catch (err) {
      console.error(err)
    }
  }, [])

  useEffect(() => {
    fetchWrestlers()
  }, [fetchWrestlers])

  useEffect(() => {
    let isMounted = true

    const fetchChampionTitleStats = async () => {
      if (!showTitleDashboard && !showTitleModal) return

      const championWrestlers = wrestlers.filter(
        wrestler =>
          Array.isArray(wrestler.championTypes) &&
          wrestler.championTypes.some(Boolean)
      )

      if (championWrestlers.length === 0) {
        if (isMounted) {
          setChampionTitleStats({})
          setChampionTitleBonusHistory({})
          setShowsSchedule([])
        }
        return
      }

      const titleValuesByWrestler = championWrestlers.reduce(
        (acc, wrestler) => {
          acc[wrestler.id] = wrestler.championTypes.filter(Boolean)
          return acc
        },
        {}
      )

      try {
        const {
          titleStatsByWrestler,
          bonusHistoryByWrestler,
          shows
        } = await loadTitleBonusSuggestionBundle({
          db,
          wrestlerIds: championWrestlers.map(wrestler => wrestler.id),
          titleValuesByWrestler
        })

        if (isMounted) {
          setChampionTitleStats(titleStatsByWrestler)
          setChampionTitleBonusHistory(bonusHistoryByWrestler)
          setShowsSchedule(shows)
        }
      } catch (error) {
        console.error('Errore nel caricamento statistiche titoli:', error)
        if (isMounted) {
          setChampionTitleStats({})
          setChampionTitleBonusHistory({})
          setShowsSchedule([])
        }
      }
    }

    fetchChampionTitleStats()

    return () => {
      isMounted = false
    }
  }, [showTitleDashboard, showTitleModal, wrestlers])

  const closeTitleModal = useCallback(() => {
    setShowTitleModal(false)
    setSelectedTitle(null)
    setSelectedWrestlers([])
    setActiveTitleTeamSuggestionId('')
    setTitleAssignmentSearch('')
    setTitleModalMode('assign')
    setAssignInterimChampion(false)
  }, [])

  const closeTitleRemovalModal = useCallback(() => {
    setShowTitleRemovalModal(false)
    setTitleRemovalTitle(null)
    setTitleRemovalChampions([])
    setTitleRemovalSubmittingId('')
  }, [])

  const openTitleRemovalModal = useCallback((championship, champions = []) => {
    setTitleRemovalTitle(championship)
    setTitleRemovalChampions(champions)
    setTitleRemovalSubmittingId('')
    setShowTitleRemovalModal(true)
  }, [])

  const getTitleImageForWrestler = useCallback((title, wrestler) => {
    if (!title || !wrestler) return ''

    if (title.type === 'mixed_tag_team') {
      if (wrestler.gender === 'Uomo') {
        return title.imageMale || title.image || ''
      }

      if (wrestler.gender === 'Donna') {
        return title.imageFemale || title.image || ''
      }
    }

    return title.image || ''
  }, [])

  // MODIFICA QUI: handleSubmit aggiornato
  const handleSubmit = async e => {
    e.preventDefault()
    if (!name || !imageUrl) {
      setMessage('Compila i campi obbligatori: nome, immagine')
      return
    }
    if (!isGuest && (!gender || !alignment)) {
      setMessage(
        'Compila tutti i campi obbligatori: nome, immagine, sesso, allineamento'
      )
      return
    }

    try {
      const cleanChampionTypes = championTypes.filter(type => type !== '')
      const champImgs = cleanChampionTypes
        .map(type => {
          const selectedChampionship = CHAMPIONSHIPS.find(c => c.value === type)
          if (!selectedChampionship) return ''

          if (selectedChampionship.type === 'mixed_tag_team') {
            if (gender === 'Uomo') {
              return (
                selectedChampionship.imageMale ||
                selectedChampionship.image ||
                ''
              )
            } else if (gender === 'Donna') {
              return (
                selectedChampionship.imageFemale ||
                selectedChampionship.image ||
                ''
              )
            }
          }

          return selectedChampionship.image || ''
        })
        .filter(Boolean)

      // Costruisci i dati base del wrestler
      const previousWrestler = editingId
        ? wrestlers.find(item => item.id === editingId)
        : null
      const championStatusByTitle = normalizeChampionStatusByTitle(
        previousWrestler?.championStatusByTitle,
        cleanChampionTypes
      )
      const wrestlerData = {
        name,
        imageUrl,
        score: editingId ? Number(score) || 0 : 0,
        brand: brand || 'Free Agent',
        gender,
        updatedAt: serverTimestamp(),
        championTypes: cleanChampionTypes,
        championImages: champImgs,
        championStatusByTitle,
        isInjured,
        alignment,
        active,
        isGuest,
        premiumComment: premiumComment.trim(),
        position: editingId ? previousWrestler?.position ?? 0 : 0,
        storyline: editingId ? previousWrestler?.storyline ?? 0 : 0
      }

      if (editingId) {
        // MODIFICA: In modifica, NON aggiornare price e prices
        // (anche quando un ospite diventa fisso o viceversa, gli altri campi
        // restano quelli gia' salvati: non vengono azzerati passando a ospite)
        await updateDoc(doc(db, 'wrestlers', editingId), wrestlerData)
        await recordAdminUpdate({
          type: 'Wrestler',
          action: 'Modifica',
          title: name,
          description: buildChangeDescription('Wrestler modificato', previousWrestler, wrestlerData, [
            { key: 'name', label: 'nome' },
            { key: 'imageUrl', label: 'immagine', renderChange: () => 'immagine aggiornata' },
            { key: 'brand', label: 'brand(roster)' },
            { key: 'gender', label: 'sesso' },
            { key: 'alignment', label: 'allineamento' },
            { key: 'isInjured', label: 'infortunato', format: formatAdminBoolean },
            { key: 'active', label: 'attivo', format: formatAdminBoolean },
            { key: 'premiumComment', label: 'commento premium' },
            {
              key: 'championTypes',
              label: 'titoli',
              format: value => Array.isArray(value) && value.length ? value.join(', ') : 'nessuno'
            }
          ]),
          entityType: 'wrestlers',
          entityId: editingId
        })
        setMessage('Wrestler modificato!')
      } else if (isGuest) {
        // Ospite: doc minimale, solo nome e immagine, per non appesantire
        // il db con campi che poi andrebbero comunque riempiti in fase di
        // promozione a wrestler fisso.
        const now = new Date()
        const wrestlerRef = await addDoc(collection(db, 'wrestlers'), {
          name,
          imageUrl,
          isGuest: true,
          active: false,
          createdAt: now,
          updatedAt: serverTimestamp()
        })
        await recordAdminUpdate({
          type: 'Wrestler',
          action: 'Creazione',
          title: name,
          description: `Nuovo ospite creato: ${name}`,
          entityType: 'wrestlers',
          entityId: wrestlerRef.id
        })
        setMessage('Ospite aggiunto!')
      } else {
        // MODIFICA: In creazione, inizializza price a 1 e prices con primo valore
        const now = new Date()
        const wrestlerRef = await addDoc(collection(db, 'wrestlers'), {
          ...wrestlerData,
          price: 1, // Inizializzato a 1
          prices: [{ value: 1, date: now }], // Array con primo valore 1
          createdAt: now
        })
        await recordAdminUpdate({
          type: 'Wrestler',
          action: 'Creazione',
          title: name,
          description: `Nuovo wrestler creato: ${name}`,
          entityType: 'wrestlers',
          entityId: wrestlerRef.id
        })
        setMessage('Wrestler aggiunto!')
      }

      await fetchWrestlers()
      resetForm()
    } catch (err) {
      console.error("Errore nell'operazione:", err)
      setMessage(`Errore nell'operazione: ${err.message}`)
    }
  }

  const resetForm = () => {
    setEditingId(null)
    setExpandedEditId(null)
    setShowCreateForm(false)
    setName('')
    setImageUrl('')
    setScore('')
    setBrand('Free Agent')
    setGender('')
    setChampionTypes([''])
    setIsInjured(false)
    setUseImageUpload(false)
    setAlignment('')
    setActive(true)
    setIsGuest(false)
    setPremiumComment('')
  }

  const handleToggleCreateForm = () => {
    if (showCreateForm) {
      resetForm()
      return
    }

    resetForm()
    setShowCreateForm(true)
  }

  const handleEdit = wrestler => {
    if (editingId === wrestler.id && expandedEditId === wrestler.id) {
      resetForm()
      return
    }

    setEditingId(wrestler.id)
    setExpandedEditId(wrestler.id)
    setShowCreateForm(false)
    setName(wrestler.name)
    setImageUrl(wrestler.imageUrl)
    setScore(wrestler.score)
    setBrand(wrestler.brand || 'Free Agent')
    setGender(wrestler.gender || '')
    setAlignment(wrestler.alignment)
    setActive(wrestler.active !== undefined ? wrestler.active : true)
    setIsInjured(wrestler.isInjured || false)
    setIsGuest(!!wrestler.isGuest)
    setPremiumComment(wrestler.premiumComment || '')

    if (wrestler.championTypes && wrestler.championTypes.length > 0) {
      setChampionTypes([...wrestler.championTypes])
    } else {
      setChampionTypes([''])
    }
  }

  const handleDelete = async id => {
    if (window.confirm('Sei sicuro di voler eliminare questo wrestler?')) {
      try {
        const wrestler = wrestlers.find(item => item.id === id)
        await deleteDoc(doc(db, 'wrestlers', id))
        await recordAdminUpdate({
          type: 'Wrestler',
          action: 'Eliminazione',
          title: wrestler?.name || id,
          description: `Wrestler eliminato: ${wrestler?.name || id}`,
          entityType: 'wrestlers',
          entityId: id
        })
        await fetchWrestlers()
        setMessage('Wrestler eliminato!')
      } catch (err) {
        console.error(err)
        setMessage("Errore nell'eliminazione")
      }
    }
  }

  const handleArchiveCurrentScore = async wrestler => {
    if (!wrestler?.id) return

    const seasonIndex = Array.isArray(wrestler.scores)
      ? wrestler.scores.length
      : 0
    const confirmArchive = window.confirm(
      `Salvare ${
        wrestler.score || 0
      } come punteggio Season ${seasonIndex} per ${wrestler.name}?`
    )

    if (!confirmArchive) return

    try {
      const wrestlerRef = doc(db, 'wrestlers', wrestler.id)
      const wrestlerSnap = await getDoc(wrestlerRef)
      if (!wrestlerSnap.exists()) {
        setMessage('Errore: wrestler non trovato')
        return
      }

      const data = wrestlerSnap.data()
      const currentScores = getSeasonScores(data)
      const updatedScores = [...currentScores, Number(data.score || 0)]

      await updateDoc(wrestlerRef, {
        scores: updatedScores,
        updatedAt: serverTimestamp()
      })

      setMessage(
        `Punteggio salvato come Season ${currentScores.length} per ${
          data.name || wrestler.name
        }.`
      )
      await fetchWrestlers()
    } catch (err) {
      console.error(err)
      setMessage(
        `Errore nel salvataggio del punteggio stagionale: ${err.message}`
      )
    }
  }

  const handleArchiveAllCurrentScores = async () => {
    if (!wrestlers.length || bulkArchiveRunning || bulkResetRunning) return

    const confirmArchive = window.confirm(
      `Salvare il punteggio attuale di ${wrestlers.length} wrestler nelle rispettive nuove stagioni?`
    )

    if (!confirmArchive) return

    setBulkArchiveRunning(true)
    setBulkArchiveProgress({ current: 0, total: wrestlers.length })
    setMessage('')

    try {
      for (let index = 0; index < wrestlers.length; index += 1) {
        const wrestler = wrestlers[index]
        const wrestlerRef = doc(db, 'wrestlers', wrestler.id)
        const wrestlerSnap = await getDoc(wrestlerRef)

        if (wrestlerSnap.exists()) {
          const data = wrestlerSnap.data()
          const currentScores = getSeasonScores(data)
          const updatedScores = [...currentScores, Number(data.score || 0)]

          await updateDoc(wrestlerRef, {
            scores: updatedScores,
            updatedAt: serverTimestamp()
          })
        }

        setBulkArchiveProgress({ current: index + 1, total: wrestlers.length })
      }

      setMessage(
        `Punteggi stagionali salvati per ${wrestlers.length} wrestler.`
      )
      await fetchWrestlers()
    } catch (err) {
      console.error(err)
      setMessage(`Errore nel salvataggio massivo dei punteggi: ${err.message}`)
    } finally {
      setBulkArchiveRunning(false)
    }
  }

  const handleResetAllCurrentScores = async () => {
    if (!wrestlers.length || bulkResetRunning || bulkArchiveRunning) return

    const confirmReset = window.confirm(
      `Azzerare il campo score di ${wrestlers.length} wrestler? L'array scores non verra modificato.`
    )

    if (!confirmReset) return

    setBulkResetRunning(true)
    setBulkArchiveProgress({ current: 0, total: wrestlers.length })
    setMessage('')

    try {
      for (let index = 0; index < wrestlers.length; index += 1) {
        const wrestler = wrestlers[index]
        const wrestlerRef = doc(db, 'wrestlers', wrestler.id)

        await updateDoc(wrestlerRef, {
          score: 0,
          updatedAt: serverTimestamp()
        })

        setBulkArchiveProgress({ current: index + 1, total: wrestlers.length })
      }

      setMessage(`Campo score azzerato per ${wrestlers.length} wrestler.`)
      await fetchWrestlers()
    } catch (err) {
      console.error(err)
      setMessage(`Errore nel reset massivo dei punteggi: ${err.message}`)
    } finally {
      setBulkResetRunning(false)
    }
  }

  const filteredWrestlers = wrestlers.filter(wrestler => {
    const matchesSearch = wrestler.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
    const matchesActive =
      activeFilter === 'all' ||
      (activeFilter === 'guests' && wrestler.isGuest) ||
      (activeFilter === 'active' && wrestler.active !== false && !wrestler.isGuest) ||
      (activeFilter === 'inactive' && wrestler.active === false && !wrestler.isGuest)

    return matchesSearch && matchesActive
  })

  const handleSort = column => {
    if (sortBy === column) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(column)
      setSortOrder('asc')
    }
  }

  const displayedWrestlers = [...filteredWrestlers].sort((a, b) => {
    const getVal = (obj, col) => {
      switch (col) {
        case 'name':
          return (obj.name || '').toString().toLowerCase()
        case 'roster':
          return (obj.brand || '').toString().toLowerCase()
        case 'title':
          return obj.championTypes && obj.championTypes[0]
            ? obj.championTypes[0].toString().toLowerCase()
            : ''
        case 'status':
          return obj.isInjured ? 'infortunato' : 'active'
        case 'alignment':
          return (obj.alignment || '').toString().toLowerCase()
        default:
          return ''
      }
    }

    const va = getVal(a, sortBy)
    const vb = getVal(b, sortBy)

    let res = 0
    if (typeof va === 'number' && typeof vb === 'number') {
      res = va - vb
    } else {
      res = ('' + va).localeCompare('' + vb)
    }

    return sortOrder === 'asc' ? res : -res
  })

  // Funzione per rimuovere un titolo
  const handleRemoveTitle = async (championship, championsToRemove = null) => {
    const currentChampions = findCurrentChampions(championship)

    if (currentChampions.length === 0) {
      setMessage('Titolo già vacante!')
      return
    }

    if (
      championship.type === 'singles' &&
      currentChampions.length > 1 &&
      (!Array.isArray(championsToRemove) || championsToRemove.length === 0)
    ) {
      openTitleRemovalModal(championship, currentChampions)
      return
    }

    const championsToUpdate =
      Array.isArray(championsToRemove) && championsToRemove.length > 0
        ? currentChampions.filter(currentChampion =>
            championsToRemove.some(champion => champion.id === currentChampion.id)
          )
        : currentChampions

    if (championsToUpdate.length === 0) {
      setMessage('Nessun detentore selezionato per la rimozione.')
      return
    }

    const championNames = championsToUpdate.map(champion => champion.name).join(', ')
    if (
      window.confirm(
        `Sei sicuro di voler rimuovere il titolo "${championship.name}" da ${championNames}?`
      )
    ) {
      try {
        const championIdsToRemove = new Set(
          championsToUpdate.map(champion => champion.id)
        )
        const remainingChampions = currentChampions.filter(
          champion => !championIdsToRemove.has(champion.id)
        )
        const shouldClearRemainingInterimStatus =
          championship.type === 'singles' && remainingChampions.length <= 1

        if (championsToUpdate.length === 1) {
          setTitleRemovalSubmittingId(championsToUpdate[0].id)
        }

        const updatePromises = currentChampions.map(async champion => {
          const currentChampionTypes = Array.isArray(champion.championTypes)
            ? champion.championTypes.filter(Boolean)
            : []
          const currentChampionImages = Array.isArray(champion.championImages)
            ? champion.championImages
            : []
          const isRemovingThisChampion = championIdsToRemove.has(champion.id)
          const updatedChampionTypes = isRemovingThisChampion
            ? currentChampionTypes.filter(type => type !== championship.value)
            : currentChampionTypes
          const updatedChampionImages = isRemovingThisChampion
            ? currentChampionImages.filter(
                (_, index) => currentChampionTypes[index] !== championship.value
              )
            : currentChampionImages
          const updatedChampionStatusByTitle = {
            ...normalizeChampionStatusByTitle(
              champion.championStatusByTitle,
              updatedChampionTypes
            )
          }

          if (
            !isRemovingThisChampion &&
            shouldClearRemainingInterimStatus &&
            updatedChampionTypes.includes(championship.value)
          ) {
            delete updatedChampionStatusByTitle[championship.value]
          }

          return updateDoc(doc(db, 'wrestlers', champion.id), {
            championTypes: updatedChampionTypes,
            championImages: updatedChampionImages,
            championStatusByTitle: updatedChampionStatusByTitle
          })
        })

        await Promise.all(updatePromises)

        setMessage(`Titolo "${championship.name}" rimosso da ${championNames}!`)
        closeTitleRemovalModal()
        fetchWrestlers()
      } catch (err) {
        console.error(err)
        setMessage('Errore nella rimozione del titolo')
      } finally {
        setTitleRemovalSubmittingId('')
      }
    }
  }

  // Funzione per aprire il modal di assegnazione
  const openTitleAssignmentModal = (championship, modalMode = 'assign') => {
    // Controlla se il titolo è già assegnato
    const currentChampions = findCurrentChampions(championship)

    const nextModalMode =
      modalMode === 'replace' || currentChampions.length > 0
        ? 'replace'
        : 'assign'
    const hasCurrentInterimChampion = currentChampions.some(champion =>
      isInterimChampionForTitle(champion, championship.value)
    )

    setSelectedTitle(championship)
    setSelectedWrestlers([])
    setActiveTitleTeamSuggestionId('')
    setTitleAssignmentSearch('')
    setTitleModalMode(nextModalMode)
    setAssignInterimChampion(
      nextModalMode === 'replace' && hasCurrentInterimChampion
    )
    setShowTitleModal(true)

    // Imposta la modalità di selezione
    if (championship.type === 'mixed_tag_team') {
      setSelectionMode('mixed')
    } else if (championship.type === 'trios') {
      setSelectionMode('trios')
    } else if (championship.type === 'tag_team') {
      setSelectionMode('tag_team')
    } else {
      setSelectionMode('single')
    }
  }

  // Funzione per filtrare i wrestler per l'assegnazione titoli
  const getFilteredWrestlersForTitle = () => {
    if (!selectedTitle) return []

    const filteredWrestlers = wrestlers.filter(wrestler => {
      const wrestlerAlreadyHoldsSelectedTitle =
        Array.isArray(wrestler.championTypes) &&
        wrestler.championTypes.includes(selectedTitle.value)

      if (titleModalMode === 'replace' && wrestlerAlreadyHoldsSelectedTitle) {
        return false
      }

      // Filtra per sesso in base al titolo
      if (selectedTitle.type === 'singles') {
        if (selectedTitle.gender === 'female' && wrestler.gender !== 'Donna')
          return false
        if (selectedTitle.gender === 'male' && wrestler.gender !== 'Uomo')
          return false
      } else if (selectedTitle.type === 'tag_team') {
        // Tag team normale: stesso sesso del titolo
        if (selectedTitle.gender === 'female' && wrestler.gender !== 'Donna')
          return false
        if (selectedTitle.gender === 'male' && wrestler.gender !== 'Uomo')
          return false
      } else if (selectedTitle.type === 'trios') {
        // Trios: solo maschi come richiesto
        if (wrestler.gender !== 'Uomo') return false
      }
      // Mixed tag team: permette sia uomini che donne (la selezione sarà gestita separatamente)

      // Filtra per ricerca
      if (
        titleAssignmentSearch &&
        !wrestler.name
          .toLowerCase()
          .includes(titleAssignmentSearch.toLowerCase())
      ) {
        return false
      }

      return true
    })

    // Ordina per nome
    return filteredWrestlers.sort((a, b) => a.name.localeCompare(b.name))
  }
  void getFilteredWrestlersForTitle

  const isWrestlerEligibleForSelectedTitle = useCallback(
    (
      wrestler,
      title,
      { excludeCurrentTitleHolders = false, searchValue = '' } = {}
    ) => {
      if (!title || !wrestler) return false

      const wrestlerAlreadyHoldsSelectedTitle =
        Array.isArray(wrestler.championTypes) &&
        wrestler.championTypes.includes(title.value)

      if (excludeCurrentTitleHolders && wrestlerAlreadyHoldsSelectedTitle) {
        return false
      }

      if (title.type === 'singles') {
        if (title.gender === 'female' && wrestler.gender !== 'Donna') {
          return false
        }
        if (title.gender === 'male' && wrestler.gender !== 'Uomo') {
          return false
        }
      } else if (title.type === 'tag_team') {
        if (title.gender === 'female' && wrestler.gender !== 'Donna') {
          return false
        }
        if (title.gender === 'male' && wrestler.gender !== 'Uomo') {
          return false
        }
      } else if (title.type === 'trios' && wrestler.gender !== 'Uomo') {
        return false
      }

      if (
        searchValue &&
        !wrestler.name.toLowerCase().includes(searchValue.toLowerCase())
      ) {
        return false
      }

      return true
    },
    []
  )

  const getEnhancedFilteredWrestlersForTitle = useCallback(() => {
    if (!selectedTitle) return []

    const prioritySet = new Set(
      (savedTeams.find(team => team.id === activeTitleTeamSuggestionId)?.members ||
        []
      ).filter(Boolean)
    )

    return wrestlers
      .filter(wrestler =>
        isWrestlerEligibleForSelectedTitle(wrestler, selectedTitle, {
          excludeCurrentTitleHolders: titleModalMode === 'replace',
          searchValue: titleAssignmentSearch
        })
      )
      .sort((firstWrestler, secondWrestler) => {
        const firstPriority = prioritySet.has(firstWrestler.id) ? 1 : 0
        const secondPriority = prioritySet.has(secondWrestler.id) ? 1 : 0

        if (firstPriority !== secondPriority) {
          return secondPriority - firstPriority
        }

        return firstWrestler.name.localeCompare(secondWrestler.name)
      })
  }, [
    activeTitleTeamSuggestionId,
    isWrestlerEligibleForSelectedTitle,
    savedTeams,
    selectedTitle,
    titleAssignmentSearch,
    titleModalMode,
    wrestlers
  ])

  const getRequiredTitleSelectionCount = useCallback(title => {
    switch (title?.type) {
      case 'tag_team':
        return 2
      case 'mixed_tag_team':
        return 2
      case 'trios':
        return 3
      default:
        return 1
    }
  }, [])

  const titleTeamSuggestions = useMemo(() => {
    if (
      !selectedTitle ||
      !['tag_team', 'mixed_tag_team', 'trios'].includes(selectedTitle.type)
    ) {
      return []
    }

    const requiredCount = getRequiredTitleSelectionCount(selectedTitle)
    const compatibleWrestlers = wrestlers.filter(wrestler =>
      isWrestlerEligibleForSelectedTitle(wrestler, selectedTitle, {
        excludeCurrentTitleHolders: titleModalMode === 'replace'
      })
    )
    const compatibleWrestlersById = new Map(
      compatibleWrestlers.map(wrestler => [wrestler.id, wrestler])
    )

    return savedTeams
      .map(team => {
        const compatibleMembers = (team.members || [])
          .map(memberId => compatibleWrestlersById.get(memberId))
          .filter(Boolean)

        if (compatibleMembers.length < requiredCount) return null

        if (selectedTitle.type === 'mixed_tag_team') {
          const hasMale = compatibleMembers.some(
            wrestler => wrestler.gender === 'Uomo'
          )
          const hasFemale = compatibleMembers.some(
            wrestler => wrestler.gender === 'Donna'
          )

          if (!hasMale || !hasFemale) return null
        }

        const isExactMatch = compatibleMembers.length === requiredCount
        const canAutoSelect =
          isExactMatch &&
          (selectedTitle.type !== 'mixed_tag_team' ||
            (compatibleMembers.some(wrestler => wrestler.gender === 'Uomo') &&
              compatibleMembers.some(
                wrestler => wrestler.gender === 'Donna'
              )))

        return {
          ...team,
          compatibleMembers,
          compatibleMemberIds: compatibleMembers.map(
            wrestler => wrestler.id
          ),
          compatibleMemberNames: compatibleMembers.map(
            wrestler => wrestler.name
          ),
          isExactMatch,
          requiresManualChoice: !canAutoSelect
        }
      })
      .filter(Boolean)
      .sort((firstTeam, secondTeam) => {
        if (firstTeam.requiresManualChoice !== secondTeam.requiresManualChoice) {
          return firstTeam.requiresManualChoice ? 1 : -1
        }

        if (
          firstTeam.compatibleMembers.length !== secondTeam.compatibleMembers.length
        ) {
          return (
            firstTeam.compatibleMembers.length -
            secondTeam.compatibleMembers.length
          )
        }

        return firstTeam.name.localeCompare(secondTeam.name)
      })
  }, [
    getRequiredTitleSelectionCount,
    isWrestlerEligibleForSelectedTitle,
    savedTeams,
    selectedTitle,
    titleModalMode,
    wrestlers
  ])

  const activeTitleTeamSuggestion = useMemo(
    () =>
      titleTeamSuggestions.find(team => team.id === activeTitleTeamSuggestionId) ||
      null,
    [activeTitleTeamSuggestionId, titleTeamSuggestions]
  )

  const handleTitleTeamSuggestionSelect = teamSuggestion => {
    if (!selectedTitle || !teamSuggestion) return

    const requiredCount = getRequiredTitleSelectionCount(selectedTitle)
    setActiveTitleTeamSuggestionId(teamSuggestion.id)
    setTitleAssignmentSearch('')

    if (
      teamSuggestion.isExactMatch &&
      !teamSuggestion.requiresManualChoice
    ) {
      setSelectedWrestlers(
        teamSuggestion.compatibleMembers.slice(0, requiredCount)
      )
      setMessage(`Team "${teamSuggestion.name}" selezionato automaticamente.`)
      return
    }

    setSelectedWrestlers(prev =>
      prev
        .filter(wrestler =>
          teamSuggestion.compatibleMemberIds.includes(wrestler.id)
        )
        .slice(0, requiredCount)
    )
    setMessage(
      `Team "${teamSuggestion.name}" attivo: scegli ${requiredCount} wrestler tra i membri suggeriti.`
    )
  }

  /* eslint-disable no-unused-vars */
  // Funzione per assegnare un titolo
  const handleAssignTitleLegacy = async () => {
    if (!selectedTitle || selectedWrestlers.length === 0) return

    const requiredCount = getRequiredWrestlersCount(selectedTitle)
    if (selectedWrestlers.length !== requiredCount) {
      setMessage(
        `Seleziona esattamente ${requiredCount} wrestler per questo titolo`
      )
      return
    }

    // Per mixed tag team: controlla che ci sia 1 uomo e 1 donna
    if (selectedTitle.type === 'mixed_tag_team') {
      const maleCount = selectedWrestlers.filter(
        w => w.gender === 'Uomo'
      ).length
      const femaleCount = selectedWrestlers.filter(
        w => w.gender === 'Donna'
      ).length

      if (maleCount !== 1 || femaleCount !== 1) {
        setMessage('Mixed Tag Team richiede esattamente 1 uomo e 1 donna')
        return
      }
    }

    try {
      const currentChampions = findCurrentChampions(selectedTitle)
      const currentChampionGroups = getTitleChampionGroups(
        currentChampions,
        selectedTitle.value
      )
      const currentOfficialChampionIds = new Set(
        currentChampionGroups.official.map(champion => champion.id)
      )
      const currentInterimChampionIds = new Set(
        currentChampionGroups.interim.map(champion => champion.id)
      )
      const selectedWrestlerIds = new Set(
        selectedWrestlers.map(wrestler => wrestler.id)
      )

      if (
        titleModalMode === 'replace' &&
        currentChampions.some(champion => selectedWrestlerIds.has(champion.id))
      ) {
        setMessage(
          'Seleziona un nuovo detentore: il campione attuale non puo essere riselezionato da questa schermata.'
        )
        return
      }

      const wrestlerIdsToUpdate = [
        ...new Set([
          ...currentChampions.map(champion => champion.id),
          ...selectedWrestlers.map(wrestler => wrestler.id)
        ])
      ]

      const updatePromises = wrestlerIdsToUpdate.map(async wrestlerId => {
        const wrestler = wrestlers.find(item => item.id === wrestlerId)
        if (!wrestler) return null

        const currentChampionTypes = Array.isArray(wrestler.championTypes)
          ? wrestler.championTypes.filter(Boolean)
          : []
        const currentChampionImages = Array.isArray(wrestler.championImages)
          ? wrestler.championImages
          : []
        const currentlyHasTitle = currentChampionTypes.includes(
          selectedTitle.value
        )
        const shouldHoldTitle = assignInterimChampion
          ? selectedWrestlerIds.has(wrestlerId) ||
            currentOfficialChampionIds.has(wrestlerId)
          : selectedWrestlerIds.has(wrestlerId)
        const shouldKeepAsOfficialChampion =
          assignInterimChampion &&
          currentOfficialChampionIds.has(wrestlerId) &&
          !selectedWrestlerIds.has(wrestlerId)
        const shouldClearPreviousInterimChampion =
          assignInterimChampion &&
          currentInterimChampionIds.has(wrestlerId) &&
          !selectedWrestlerIds.has(wrestlerId)

        let updatedChampionTypes = currentChampionTypes
        let updatedChampionImages = currentChampionImages

        if (
          currentlyHasTitle &&
          (!shouldHoldTitle || shouldClearPreviousInterimChampion)
        ) {
          updatedChampionTypes = currentChampionTypes.filter(
            type => type !== selectedTitle.value
          )
          updatedChampionImages = currentChampionImages.filter(
            (_, index) => currentChampionTypes[index] !== selectedTitle.value
          )
        } else if (!currentlyHasTitle && shouldHoldTitle) {
          updatedChampionTypes = [
            ...currentChampionTypes,
            selectedTitle.value
          ]
          updatedChampionImages = [
            ...currentChampionImages,
            getTitleImageForWrestler(selectedTitle, wrestler)
          ]
        }

        const updatedChampionStatusByTitle = {
          ...normalizeChampionStatusByTitle(
            wrestler.championStatusByTitle,
            updatedChampionTypes
          )
        }

        if (shouldKeepAsOfficialChampion) {
          delete updatedChampionStatusByTitle[selectedTitle.value]
        } else if (
          shouldHoldTitle &&
          assignInterimChampion &&
          selectedWrestlerIds.has(wrestlerId)
        ) {
          updatedChampionStatusByTitle[selectedTitle.value] =
            INTERIM_CHAMPION_STATUS
        } else {
          delete updatedChampionStatusByTitle[selectedTitle.value]
        }

        return updateDoc(doc(db, 'wrestlers', wrestler.id), {
          championTypes: updatedChampionTypes,
          championImages: updatedChampionImages,
          championStatusByTitle: updatedChampionStatusByTitle
        })
      })

      await Promise.all(updatePromises.filter(Boolean))

      const wrestlerNames = selectedWrestlers.map(w => w.name).join(', ')
      const actionVerb =
        currentChampions.length > 0 ? 'aggiornato' : 'assegnato'
      const statusSuffix = assignInterimChampion
        ? ' come campione provvisorio'
        : ''
      setMessage(
        `Titolo "${selectedTitle.name}" ${actionVerb} a ${wrestlerNames}${statusSuffix}!`
      )
      closeTitleModal()
      fetchWrestlers()
    } catch (err) {
      console.error(err)
      setMessage("Errore nell'assegnazione del titolo")
    }
  }

  const handleAssignTitle = async () => {
    if (!selectedTitle || selectedWrestlers.length === 0) return

    const requiredCount = getRequiredWrestlersCount(selectedTitle)
    if (selectedWrestlers.length !== requiredCount) {
      setMessage(
        `Seleziona esattamente ${requiredCount} wrestler per questo titolo`
      )
      return
    }

    if (selectedTitle.type === 'mixed_tag_team') {
      const maleCount = selectedWrestlers.filter(
        wrestler => wrestler.gender === 'Uomo'
      ).length
      const femaleCount = selectedWrestlers.filter(
        wrestler => wrestler.gender === 'Donna'
      ).length

      if (maleCount !== 1 || femaleCount !== 1) {
        setMessage('Mixed Tag Team richiede esattamente 1 uomo e 1 donna')
        return
      }
    }

    try {
      const currentChampions = findCurrentChampions(selectedTitle)
      const currentChampionGroups = getTitleChampionGroups(
        currentChampions,
        selectedTitle.value
      )
      const currentOfficialChampionIds = new Set(
        currentChampionGroups.official.map(champion => champion.id)
      )
      const currentInterimChampionIds = new Set(
        currentChampionGroups.interim.map(champion => champion.id)
      )
      const selectedWrestlerIds = new Set(
        selectedWrestlers.map(wrestler => wrestler.id)
      )

      if (
        titleModalMode === 'replace' &&
        currentChampions.some(champion => selectedWrestlerIds.has(champion.id))
      ) {
        setMessage(
          'Seleziona un nuovo detentore: il campione attuale non puo essere riselezionato da questa schermata.'
        )
        return
      }

      const wrestlerIdsToUpdate = [
        ...new Set([
          ...currentChampions.map(champion => champion.id),
          ...selectedWrestlers.map(wrestler => wrestler.id)
        ])
      ]

      const updatePromises = wrestlerIdsToUpdate.map(async wrestlerId => {
        const wrestler = wrestlers.find(item => item.id === wrestlerId)
        if (!wrestler) return null

        const currentChampionTypes = Array.isArray(wrestler.championTypes)
          ? wrestler.championTypes.filter(Boolean)
          : []
        const currentChampionImages = Array.isArray(wrestler.championImages)
          ? wrestler.championImages
          : []
        const currentlyHasTitle = currentChampionTypes.includes(
          selectedTitle.value
        )
        const shouldHoldTitle = assignInterimChampion
          ? selectedWrestlerIds.has(wrestlerId) ||
            currentOfficialChampionIds.has(wrestlerId)
          : selectedWrestlerIds.has(wrestlerId)
        const shouldKeepAsOfficialChampion =
          assignInterimChampion &&
          currentOfficialChampionIds.has(wrestlerId) &&
          !selectedWrestlerIds.has(wrestlerId)
        const shouldClearPreviousInterimChampion =
          assignInterimChampion &&
          currentInterimChampionIds.has(wrestlerId) &&
          !selectedWrestlerIds.has(wrestlerId)

        let updatedChampionTypes = currentChampionTypes
        let updatedChampionImages = currentChampionImages

        if (
          currentlyHasTitle &&
          (!shouldHoldTitle || shouldClearPreviousInterimChampion)
        ) {
          updatedChampionTypes = currentChampionTypes.filter(
            type => type !== selectedTitle.value
          )
          updatedChampionImages = currentChampionImages.filter(
            (_, index) => currentChampionTypes[index] !== selectedTitle.value
          )
        } else if (!currentlyHasTitle && shouldHoldTitle) {
          updatedChampionTypes = [
            ...currentChampionTypes,
            selectedTitle.value
          ]
          updatedChampionImages = [
            ...currentChampionImages,
            getTitleImageForWrestler(selectedTitle, wrestler)
          ]
        }

        const updatedChampionStatusByTitle = {
          ...normalizeChampionStatusByTitle(
            wrestler.championStatusByTitle,
            updatedChampionTypes
          )
        }

        if (shouldKeepAsOfficialChampion) {
          delete updatedChampionStatusByTitle[selectedTitle.value]
        } else if (
          shouldHoldTitle &&
          assignInterimChampion &&
          selectedWrestlerIds.has(wrestlerId)
        ) {
          updatedChampionStatusByTitle[selectedTitle.value] =
            INTERIM_CHAMPION_STATUS
        } else {
          delete updatedChampionStatusByTitle[selectedTitle.value]
        }

        return updateDoc(doc(db, 'wrestlers', wrestler.id), {
          championTypes: updatedChampionTypes,
          championImages: updatedChampionImages,
          championStatusByTitle: updatedChampionStatusByTitle
        })
      })

      await Promise.all(updatePromises.filter(Boolean))

      const wrestlerNames = selectedWrestlers.map(w => w.name).join(', ')
      const actionVerb =
        currentChampions.length > 0 ? 'aggiornato' : 'assegnato'
      const statusSuffix = assignInterimChampion
        ? ' come campione provvisorio'
        : ''
      setMessage(
        `Titolo "${selectedTitle.name}" ${actionVerb} a ${wrestlerNames}${statusSuffix}!`
      )
      closeTitleModal()
      fetchWrestlers()
    } catch (err) {
      console.error(err)
      setMessage("Errore nell'assegnazione del titolo")
    }
  }
  /* eslint-enable no-unused-vars */

  void handleAssignTitleLegacy

  const getRequiredWrestlersCount = useCallback(title => {
    switch (title.type) {
      case 'tag_team':
        return 2
      case 'mixed_tag_team':
        return 2 // 1 uomo + 1 donna
      case 'trios':
        return 3
      default:
        return 1
    }
  }, [])

  // Trova tutti i campioni di un titolo
  const findCurrentChampions = useCallback(title => {
    return wrestlers.filter(
      wrestler =>
        wrestler.championTypes &&
        wrestler.championTypes.includes(title.value)
    )
  }, [wrestlers])

  const currentTitleChampions = useMemo(
    () => (selectedTitle ? findCurrentChampions(selectedTitle) : []),
    [findCurrentChampions, selectedTitle]
  )

  const currentTitleIsInterim = useMemo(
    () =>
      Boolean(
        selectedTitle &&
          currentTitleChampions.some(champion =>
            isInterimChampionForTitle(champion, selectedTitle.value)
          )
      ),
    [currentTitleChampions, selectedTitle]
  )

  const currentTitleChangePenaltySuggestions = useMemo(() => {
    if (!selectedTitle || titleModalMode !== 'replace' || assignInterimChampion) {
      return []
    }

    return buildTitleChangePenaltySuggestions({
      titleValue: selectedTitle.value,
      currentChampions: currentTitleChampions,
      titleStatsByWrestler: championTitleStats,
      bonusHistoryByWrestler: championTitleBonusHistory,
      shows: showsSchedule,
      referenceDate: new Date()
    })
  }, [
    assignInterimChampion,
    championTitleBonusHistory,
    championTitleStats,
    currentTitleChampions,
    selectedTitle,
    showsSchedule,
    titleModalMode
  ])

  const getChampionTitleStat = (wrestlerId, titleValue) =>
    championTitleStats[wrestlerId]?.[titleValue] || null

  const renderTitleBackdrop = championship => {
    if (championship.type === 'mixed_tag_team') {
      return (
        <div className='title-dashboard-belt-split'>
          <div className='title-dashboard-belt-image'>
            {championship.imageMale ? (
              <img
                src={championship.imageMale}
                alt={`${championship.name} (Uomo)`}
                onError={e => {
                  e.target.onerror = null
                  e.target.style.display = 'none'
                }}
              />
            ) : null}
          </div>
          <div className='title-dashboard-belt-image'>
            {championship.imageFemale ? (
              <img
                src={championship.imageFemale}
                alt={`${championship.name} (Donna)`}
                onError={e => {
                  e.target.onerror = null
                  e.target.style.display = 'none'
                }}
              />
            ) : null}
          </div>
        </div>
      )
    }

    if (championship.image) {
      return (
        <div className='title-dashboard-belt-image'>
          <img
            src={championship.image}
            alt={championship.name}
            onError={e => {
              e.target.onerror = null
              e.target.style.display = 'none'
            }}
          />
        </div>
      )
    }

    return (
      <div className='title-dashboard-belt-image'>
        <i className='bi bi-trophy'></i>
      </div>
    )
  }

  const renderChampionTitleDays = (wrestlerId, titleValue) => {
    const championship = CHAMPIONSHIPS.find(
      currentChampionship => currentChampionship.value === titleValue
    )

    if (championship?.isTitle === false) return null

    const stat = getChampionTitleStat(wrestlerId, titleValue)
    const championDays = stat?.wonAt ? stat.daysAsChampion : ''
    const defenseDays = stat?.defendedAt ? stat.daysSinceLastDefense : ''
    const championDaysClass = isChampionDaysInBonusWindow(championDays)
      ? 'title-dashboard-champion-days__value--milestone'
      : ''

    return (
      <div
        className='title-dashboard-champion-days'
        title="C = giorni da campione, D = giorni dall'ultima difesa titolata"
      >
        <span className='title-dashboard-champion-days__item'>
          <span className='title-dashboard-champion-days__label'>G</span>
          <span className={`title-dashboard-champion-days__value ${championDaysClass}`}>
            {championDays !== '' ? championDays : '\u00A0'}
          </span>
        </span>
        <span className='title-dashboard-champion-days__item'>
          <span className='title-dashboard-champion-days__label'>D</span>
          <span className={`title-dashboard-champion-days__value ${defenseDays >= 85 ? 'text-brand-accent' : ''}`}>
            {defenseDays !== '' ? defenseDays : '\u00A0'}
          </span>
        </span>
      </div>
    )
  }

  const getTitleChampionGroups = useCallback(
    (champions = [], titleValue = '') => {
      const official = []
      const interim = []

      champions.forEach(champion => {
        if (isInterimChampionForTitle(champion, titleValue)) {
          interim.push(champion)
        } else {
          official.push(champion)
        }
      })

      return {
        official,
        interim,
        ordered: [...official, ...interim]
      }
    },
    []
  )

  const matchesTitleTypeFilter = useCallback(
    championship => {
      if (titleFilter === 'all') return true
      if (titleFilter === 'tag_team') {
        return (
          championship.type === 'tag_team' ||
          championship.type === 'mixed_tag_team' ||
          championship.type === 'trios'
        )
      }

      return championship.type === titleFilter
    },
    [titleFilter]
  )

  const matchesTitleGenderFilter = useCallback(
    championship => {
      if (titleGenderFilter === 'all') return true
      return championship.gender === titleGenderFilter
    },
    [titleGenderFilter]
  )

  const matchesTitleUniverseFilter = useCallback(
    championship => {
      if (titleUniverseFilter === 'all') return true

      const isExtraTitle =
        championship.value.startsWith('TNA ') ||
        championship.value.startsWith('AAA ')

      return titleUniverseFilter === 'extra' ? isExtraTitle : !isExtraTitle
    },
    [titleUniverseFilter]
  )

  const titleDashboardCards = useMemo(() => {
    return CHAMPIONSHIPS.map((championship, originalIndex) => ({
      championship,
      originalIndex
    }))
      .filter(({ championship }) => championship.value !== '')
      .filter(({ championship }) => matchesTitleTypeFilter(championship))
      .filter(({ championship }) => matchesTitleGenderFilter(championship))
      .filter(({ championship }) => matchesTitleUniverseFilter(championship))
      .map(({ championship, originalIndex }) => {
        const currentChampions = findCurrentChampions(championship)

        return {
          championship,
          originalIndex,
          currentChampions,
          requiredCount: getRequiredWrestlersCount(championship)
        }
      })
      .sort((left, right) => {
        const leftOccupied = left.currentChampions.length > 0 ? 1 : 0
        const rightOccupied = right.currentChampions.length > 0 ? 1 : 0

        if (leftOccupied !== rightOccupied) {
          return rightOccupied - leftOccupied
        }

        return left.originalIndex - right.originalIndex
      })
  }, [
    findCurrentChampions,
    getRequiredWrestlersCount,
    matchesTitleGenderFilter,
    matchesTitleTypeFilter,
    matchesTitleUniverseFilter
  ])

  /* eslint-disable no-unused-vars */
  const renderTitleDashboardCardLegacy = ({
    championship,
    currentChampions,
    requiredCount
  }) => {
    const championGroups = getTitleChampionGroups(
      currentChampions,
      championship.value
    )
    const displayedChampions = championGroups.ordered

    const renderChampionList = champions => (
      <div className='d-flex flex-column gap-1'>
        {champions.map(champion => {
          const isSingleChampionBlock =
            champions.length === 1 && requiredCount === 1

          return (
            <div key={champion.id} className='title-dashboard-champion-entry'>
              <div
                className={`fw-bold title-dashboard-champion-name ${
                  isSingleChampionBlock
                    ? 'title-dashboard-champion-name--single'
                    : ''
                }`}
              >
                {champion.name}
              </div>
              {renderChampionTitleDays(champion.id, championship.value)}
            </div>
          )
        })}
      </div>
    )

    return (
      <div className='col' key={championship.value}>
        <div
          className={`card h-100 shadow-sm title-dashboard-card ${
            displayedChampions.length > 0
              ? 'title-dashboard-card--occupied'
              : 'title-dashboard-card--vacant'
          }`}
        >
          <div className='card-body text-center d-flex flex-column title-dashboard-card__body'>
            <div className='title-dashboard-hero'>
              <div className='title-dashboard-belt-backdrop'>
                {renderTitleBackdrop(championship)}
              </div>

              {displayedChampions.length > 0 ? (
                <div
                  className={`title-dashboard-champion-stack ${
                    displayedChampions.length === 2
                      ? 'title-dashboard-champion-stack--pair'
                      : displayedChampions.length >= 3
                      ? 'title-dashboard-champion-stack--grid'
                      : ''
                  }`}
                  style={{
                    '--title-dashboard-avatar-size':
                      displayedChampions.length === 1
                        ? '84px'
                        : displayedChampions.length === 2
                        ? '60px'
                        : displayedChampions.length === 3
                        ? '48px'
                        : displayedChampions.length === 4
                        ? '42px'
                        : '36px',
                    '--title-dashboard-avatar-columns':
                      displayedChampions.length <= 2
                        ? displayedChampions.length
                        : displayedChampions.length <= 4
                        ? 2
                        : 3
                  }}
                >
                  {displayedChampions.map(champion => {
                    const isInterimChampion = isInterimChampionForTitle(
                      champion,
                      championship.value
                    )

                    return (
                      <div
                        key={champion.id}
                        className='title-dashboard-champion-avatar-frame'
                      >
                        <img
                          src={champion.imageUrl}
                          alt={champion.name}
                          className={`rounded-circle border border-2 title-dashboard-champion-avatar ${
                            isInterimChampion
                              ? 'title-dashboard-champion-avatar--interim'
                              : 'title-dashboard-champion-avatar--official'
                          }`}
                          onError={e => {
                            e.target.onerror = null
                            e.target.src =
                              'https://via.placeholder.com/80x80?text=W'
                          }}
                        />
                        {isInterimChampion && (
                          <span className='title-dashboard-champion-avatar-tag'>
                            Interim
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className='title-dashboard-champion-stack'>
                  <span className='badge rounded-pill bg-secondary text-white border border-light border-opacity-25'>
                    VACANT
                  </span>
                </div>
              )}
            </div>

            <div className='title-dashboard-info'>
              {displayedChampions.length > 0 ? (
                renderChampionList(displayedChampions)
              ) : (
                <div className='fw-bold title-dashboard-vacant-name'>
                  {championship.name}
                </div>
              )}

              {displayedChampions.length > 0 && (
                <div className='text-muted small mt-1'>{championship.name}</div>
              )}
            </div>

            <div className='mt-auto title-dashboard-actions'>
              {displayedChampions.length > 0 ? (
                <>
                  <button
                    className='btn btn-outline-warning btn-sm flex-grow-1'
                    onClick={() =>
                      openTitleAssignmentModal(championship, 'replace')
                    }
                    title={`Cambia detentore di ${championship.name}`}
                  >
                    <i className='bi bi-arrow-repeat me-1'></i>
                    Cambia
                  </button>
                  <button
                    className='btn btn-outline-danger btn-sm title-dashboard-remove-button'
                    onClick={() => handleRemoveTitle(championship)}
                    title='Rimuovi titolo'
                    aria-label={`Rimuovi ${championship.name}`}
                  >
                    <i className='bi bi-x-circle'></i>
                  </button>
                </>
              ) : (
                <button
                  className='btn btn-outline-success btn-sm w-100'
                  onClick={() => openTitleAssignmentModal(championship)}
                  title={`Assegna ${championship.name}`}
                >
                  <i className='bi bi-plus-circle me-1'></i>
                  Assegna
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Controlla se un wrestler è già selezionato
  void renderTitleDashboardCardLegacy
  /* eslint-enable no-unused-vars */

  const renderTitleDashboardCard = ({
    championship,
    currentChampions,
    requiredCount
  }) => {
    const championGroups = getTitleChampionGroups(
      currentChampions,
      championship.value
    )
    const displayedChampions = championGroups.ordered

    const renderChampionList = champions => (
      <div className='d-flex flex-column gap-1'>
        {champions.map(champion => {
          const isSingleChampionBlock =
            champions.length === 1 && requiredCount === 1

          return (
            <div key={champion.id} className='title-dashboard-champion-entry'>
              <div
                className={`fw-bold title-dashboard-champion-name ${
                  isSingleChampionBlock
                    ? 'title-dashboard-champion-name--single'
                    : ''
                }`}
              >
                {champion.name}
              </div>
              {renderChampionTitleDays(champion.id, championship.value)}
            </div>
          )
        })}
      </div>
    )

    return (
      <div className='col' key={championship.value}>
        <div
          className={`card h-100 shadow-sm title-dashboard-card ${
            currentChampions.length > 0
              ? 'title-dashboard-card--occupied'
              : 'title-dashboard-card--vacant'
          }`}
        >
          <div className='card-body text-center d-flex flex-column title-dashboard-card__body'>
            <div className='title-dashboard-hero'>
              <div className='title-dashboard-belt-backdrop'>
                {renderTitleBackdrop(championship)}
              </div>

              {displayedChampions.length > 0 ? (
                <div
                  className={`title-dashboard-champion-stack ${
                    displayedChampions.length === 2
                      ? 'title-dashboard-champion-stack--pair'
                      : displayedChampions.length >= 3
                      ? 'title-dashboard-champion-stack--grid'
                      : ''
                  }`}
                  style={{
                    '--title-dashboard-avatar-size':
                      displayedChampions.length === 1
                        ? '84px'
                        : displayedChampions.length === 2
                        ? '60px'
                        : displayedChampions.length === 3
                        ? '48px'
                        : displayedChampions.length === 4
                        ? '42px'
                        : '36px',
                    '--title-dashboard-avatar-columns':
                      displayedChampions.length <= 2
                        ? displayedChampions.length
                        : displayedChampions.length <= 4
                        ? 2
                        : 3
                  }}
                >
                  {displayedChampions.map(champion => {
                    const isInterimChampion = isInterimChampionForTitle(
                      champion,
                      championship.value
                    )

                    return (
                      <div
                        key={champion.id}
                        className='title-dashboard-champion-avatar-frame'
                      >
                        <img
                          src={champion.imageUrl}
                          alt={champion.name}
                          className={`rounded-circle border border-2 title-dashboard-champion-avatar ${
                            isInterimChampion
                              ? 'title-dashboard-champion-avatar--interim'
                              : 'title-dashboard-champion-avatar--official'
                          }`}
                          onError={e => {
                            e.target.onerror = null
                            e.target.src =
                              'https://via.placeholder.com/80x80?text=W'
                          }}
                        />
                        {isInterimChampion && (
                          <span className='title-dashboard-champion-avatar-tag'>
                            Interim
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className='title-dashboard-champion-stack'>
                  <span className='badge rounded-pill bg-secondary text-white border border-light border-opacity-25'>
                    VACANT
                  </span>
                </div>
              )}
            </div>

            <div className='title-dashboard-info'>
              {displayedChampions.length > 0 ? (
                renderChampionList(displayedChampions)
              ) : (
                <div className='fw-bold title-dashboard-vacant-name'>
                  {championship.name}
                </div>
              )}

              {displayedChampions.length > 0 && (
                <div className='text-muted small mt-1'>{championship.name}</div>
              )}
            </div>

            <div className='mt-auto title-dashboard-actions'>
              {displayedChampions.length > 0 ? (
                <>
                  <button
                    className='btn btn-outline-warning btn-sm flex-grow-1'
                    onClick={() =>
                      openTitleAssignmentModal(championship, 'replace')
                    }
                    title={`Cambia detentore di ${championship.name}`}
                  >
                    <i className='bi bi-arrow-repeat me-1'></i>
                    Cambia
                  </button>
                  <button
                    className='btn btn-outline-danger btn-sm title-dashboard-remove-button'
                    onClick={() => handleRemoveTitle(championship)}
                    title='Rimuovi titolo'
                    aria-label={`Rimuovi ${championship.name}`}
                  >
                    <i className='bi bi-x-circle'></i>
                  </button>
                </>
              ) : (
                <button
                  className='btn btn-outline-success btn-sm w-100'
                  onClick={() => openTitleAssignmentModal(championship)}
                  title={`Assegna ${championship.name}`}
                >
                  <i className='bi bi-plus-circle me-1'></i>
                  Assegna
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const isWrestlerSelected = wrestlerId => {
    return selectedWrestlers.some(w => w.id === wrestlerId)
  }

  // Aggiungi/rimuovi wrestler dalla selezione
  // Aggiungi/rimuovi wrestler dalla selezione
  const toggleWrestlerSelection = wrestler => {
    if (isWrestlerSelected(wrestler.id)) {
      // Rimuovi
      setSelectedWrestlers(prev => prev.filter(w => w.id !== wrestler.id))
    } else {
      // Controlla limiti in base al tipo di titolo
      const maxWrestlers = getRequiredWrestlersCount(selectedTitle)
      if (selectedWrestlers.length >= maxWrestlers) {
        setMessage(
          `Puoi selezionare al massimo ${maxWrestlers} wrestler per questo titolo`
        )
        return
      }

      // Per mixed tag team: controlla che ci sia 1 uomo e 1 donna
      if (selectedTitle.type === 'mixed_tag_team') {
        const maleCount = selectedWrestlers.filter(
          w => w.gender === 'Uomo'
        ).length
        const femaleCount = selectedWrestlers.filter(
          w => w.gender === 'Donna'
        ).length

        if (wrestler.gender === 'Uomo' && maleCount >= 1) {
          setMessage(
            'Puoi selezionare solo 1 wrestler maschio per Mixed Tag Team'
          )
          return
        }
        if (wrestler.gender === 'Donna' && femaleCount >= 1) {
          setMessage(
            'Puoi selezionare solo 1 wrestler femmina per Mixed Tag Team'
          )
          return
        }
      }

      // Aggiungi
      setSelectedWrestlers(prev => [...prev, wrestler])
    }
  }

  const getOptionKey = value =>
    value
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')

  const renderOptionGroup = ({
    label,
    required = false,
    options,
    selectedValue,
    onSelect,
    modifier = ''
  }) => (
    <div
      className={`wrestler-form-field ${
        modifier ? `wrestler-form-field--${modifier}` : ''
      }`}
    >
      <span className='wrestler-form-label'>
        {label}
        {required && <span className='text-brand-secondary ms-1'>*</span>}
      </span>
      <div className='wrestler-choice-grid'>
        {options.map(option => {
          const selected = selectedValue === option.value
          const optionKey = getOptionKey(option.value)

          return (
            <button
              type='button'
              key={option.value}
              className={`wrestler-choice wrestler-choice--${optionKey} ${
                modifier ? `wrestler-choice--${modifier}` : ''
              } ${selected ? 'is-selected' : ''}`}
              onClick={() => onSelect(option.value)}
              aria-pressed={selected}
            >
              <i className={`bi ${option.icon}`}></i>
              <span>{option.label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )

  const renderWrestlerForm = ({ mode = 'create', wrestler = null } = {}) => {
    const isEdit = mode === 'edit'
    const formId = wrestler?.id || 'new-wrestler'
    const currentSeason = Array.isArray(wrestler?.scores)
      ? wrestler.scores.length
      : 0

    return (
      <form
        onSubmit={handleSubmit}
        className='wrestler-editor-form needs-validation'
        noValidate
      >
        <div className='wrestler-form-section'>
          <button
            type='button'
            className={`wrestler-state-toggle wrestler-guest-toggle ${
              isGuest ? 'is-guest' : 'is-success'
            }`}
            onClick={() => {
              const nextIsGuest = !isGuest
              setIsGuest(nextIsGuest)
              // Promuovendo un ospite a wrestler fisso, di norma lo si vuole
              // subito visibile nelle liste pubbliche.
              if (!nextIsGuest && !active) setActive(true)
            }}
            aria-pressed={isGuest}
          >
            <span className='wrestler-state-toggle__icon'>
              <i className={`bi ${isGuest ? 'bi-person-badge-fill' : 'bi-person-vcard'}`}></i>
            </span>
            <span>
              <strong>{isGuest ? 'Ospite' : 'Wrestler fisso'}</strong>
              <small>
                {isGuest
                  ? 'Bastano nome e immagine. Convertilo in fisso quando vuoi aggiungere gli altri dati.'
                  : 'Roster completo: richiede anche sesso e allineamento.'}
              </small>
            </span>
          </button>
        </div>

        <div className='wrestler-form-section'>
          <div className='wrestler-form-section__header'>
            <div>
              <span className='wrestler-form-kicker'>Profilo</span>
            </div>
          </div>

          <div className='wrestler-form-grid wrestler-form-grid--identity'>
            <div className='wrestler-form-field'>
              <label className='wrestler-form-label' htmlFor={`name-${formId}`}>
                Nome<span className='text-brand-secondary ms-1'>*</span>
              </label>
              <input
                id={`name-${formId}`}
                type='text'
                className='form-control'
                value={name}
                onChange={e => setName(e.target.value)}
                required
                placeholder='Nome wrestler'
              />
            </div>

            <div className='wrestler-form-field wrestler-form-field--image'>
              <div className='wrestler-form-label-row'>
                <span className='wrestler-form-label mb-0'>
                  Immagine<span className='text-brand-secondary ms-1'>*</span>
                </span>
                <div
                  className='btn-group wrestler-image-mode'
                  role='group'
                  aria-label='Metodo immagine wrestler'
                >
                  <input
                    type='radio'
                    className='btn-check'
                    name={`imageType-${formId}`}
                    id={`url-${formId}`}
                    checked={!useImageUpload}
                    onChange={() => setUseImageUpload(false)}
                  />
                  <label className='btn btn-outline-primary' htmlFor={`url-${formId}`}>
                    <i className='bi bi-link-45deg'></i>
                    URL
                  </label>

                  <input
                    type='radio'
                    className='btn-check'
                    name={`imageType-${formId}`}
                    id={`upload-${formId}`}
                    checked={useImageUpload}
                    onChange={() => setUseImageUpload(true)}
                  />
                  <label className='btn btn-outline-primary' htmlFor={`upload-${formId}`}>
                    <i className='bi bi-upload'></i>
                    Upload
                  </label>
                </div>
              </div>

              <div className='wrestler-image-row'>
                <div className='wrestler-image-preview'>
                  {imageUrl ? (
                    <img src={imageUrl} alt='Anteprima wrestler' />
                  ) : (
                    <div className='wrestler-image-placeholder'>
                      <i className='bi bi-person-square'></i>
                      <span>Anteprima</span>
                    </div>
                  )}
                </div>

                <div className='wrestler-image-input'>
                  {useImageUpload ? (
                    <ImageUploader
                      onUploadSuccess={url => setImageUrl(url)}
                      currentImageUrl={imageUrl}
                      storagePath='wrestlers'
                      buttonText='Carica Immagine Wrestler'
                      showPreview={false}
                    />
                  ) : (
                    <input
                      type='url'
                      className='form-control'
                      value={imageUrl}
                      onChange={e => setImageUrl(e.target.value)}
                      required
                      placeholder='https://...'
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {!isGuest && (
        <div className='wrestler-form-section'>

          <div className='wrestler-form-grid wrestler-form-grid--classification'>
            {renderOptionGroup({
              label: 'Roster',
              required: true,
              options: BRAND_OPTIONS,
              selectedValue: brand,
              onSelect: setBrand,
              modifier: 'brand'
            })}

            {renderOptionGroup({
              label: 'Sesso',
              required: true,
              options: GENDER_OPTIONS,
              selectedValue: gender,
              onSelect: setGender,
              modifier: 'gender'
            })}

            {renderOptionGroup({
              label: 'Allineamento',
              required: true,
              options: ALIGNMENT_OPTIONS,
              selectedValue: alignment,
              onSelect: setAlignment,
              modifier: 'alignment'
            })}
          </div>
        </div>
        )}

        {!isGuest && (
        <div className='wrestler-form-section'>
          <div className='wrestler-form-section__header'>
            <div>
              <span className='wrestler-form-kicker'>Stato</span>
            </div>
          </div>

          <div className='wrestler-form-grid wrestler-form-grid--compact'>
            <button
              type='button'
              className={`wrestler-state-toggle ${
                isInjured ? 'is-danger' : 'is-success'
              }`}
              onClick={() => setIsInjured(!isInjured)}
              aria-pressed={isInjured}
            >
              <span className='wrestler-state-toggle__icon'>
                {isInjured ? (
                  <InjuryIcon size={22} iconSize='1rem' />
                ) : (
                  <i className='bi bi-heart-pulse'></i>
                )}
              </span>
              <span>
                <strong>{isInjured ? 'Infortunato' : 'Sano'}</strong>
                <small>
                  {isInjured
                    ? 'Non disponibile per i roster'
                    : 'Disponibile normalmente'}
                </small>
              </span>
            </button>

            <button
              type='button'
              className={`wrestler-state-toggle ${
                active ? 'is-success' : 'is-muted'
              }`}
              onClick={() => setActive(!active)}
              aria-pressed={active}
            >
              <span className='wrestler-state-toggle__icon'>
                <i
                  className={`bi ${
                    active ? 'bi-check-circle-fill' : 'bi-archive'
                  }`}
                ></i>
              </span>
              <span>
                <strong>{active ? 'Attivo' : 'Non attivo'}</strong>
                <small>
                  {active
                    ? 'Visibile nelle selezioni'
                    : 'Tenuto fuori dalle liste attive'}
                </small>
              </span>
            </button>

            {!isEdit && (
              <div className='wrestler-form-note'>
                <i className='bi bi-cash-coin'></i>
                <div>
                  <strong>Quotazione iniziale: 1</strong>
                  <span>La modifica resta nella pagina Gestisci Quotazioni.</span>
                </div>
              </div>
            )}
          </div>
        </div>
        )}

        {!isGuest && (
        <div className='wrestler-form-section'>
          <div className='wrestler-form-section__header'>
            <div className='d-flex flex-wrap align-items-center gap-2'>
              <span className='wrestler-form-kicker'>Premium</span>
              <PremiumFeaturePill label='Commento team' />
            </div>
          </div>
          <div className='wrestler-form-field'>
            <label className='wrestler-form-label' htmlFor={`premium-comment-${formId}`}>
              Commento breve
            </label>
            <textarea
              id={`premium-comment-${formId}`}
              className='form-control'
              rows='3'
              maxLength='180'
              value={premiumComment}
              onChange={event => setPremiumComment(event.target.value)}
              placeholder='Nota sintetica visibile agli utenti Premium'
            />
            <small className='text-muted d-block mt-2'>
              {premiumComment.length}/180 caratteri
            </small>
          </div>
        </div>
        )}

        {isGuest && (
          <div className='wrestler-form-note wrestler-guest-note'>
            <i className='bi bi-info-circle'></i>
            <div>
              <strong>Modalita' ospite</strong>
              <span>
                Roster, sesso, allineamento e quotazione si aggiungono in seguito,
                togliendo il flag "Ospite" per convertirlo in wrestler fisso.
              </span>
            </div>
          </div>
        )}

        <div className='wrestler-form-actions'>
          {isEdit && wrestler && !isGuest && (
            <button
              type='button'
              className='btn btn-outline-warning'
              onClick={() => handleArchiveCurrentScore(wrestler)}
            >
              <i className='bi bi-archive me-2'></i>
              Season {currentSeason}
            </button>
          )}
          <button
            type='button'
            className='btn btn-outline-secondary'
            onClick={resetForm}
          >
            Annulla
          </button>
          <button type='submit' className='btn btn-success wrestler-form-submit'>
            <i className='bi bi-save me-2'></i>
            {isEdit ? 'Salva modifiche' : isGuest ? 'Salva ospite' : 'Salva wrestler'}
          </button>
        </div>
      </form>
    )
  }

  return (
    <ProtectedRoute>
      <div className='add-wrestler-container admin-page py-4'>
        <div className='row justify-content-center'>
          <div className='col-lg-10 col-xl-8'>
            {message && (
              <div
                className={`alert ${
                  message.includes('Errore') ? 'alert-danger' : 'alert-success'
                } alert-dismissible fade show mb-4`}
              >
                <i
                  className={`bi ${
                    message.includes('Errore')
                      ? 'bi-exclamation-octagon'
                      : 'bi-check-circle'
                  } me-2`}
                ></i>
                {message}
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => setMessage('')}
                ></button>
              </div>
            )}

            <div className='mb-3 d-flex align-items-center'>
              <Link
                to='/admin/edit-prices'
                className='btn btn-outline-success w-100 btn-lg px-4 py-2'
              >
                Gestisci Quotazioni <i className='bi bi-arrow-right ms-2'></i>
              </Link>
            </div>

            {/* DASHBOARD TITOLI */}
            <div className='card shadow-sm mb-2'>
              <div className='card-header bg-warning text-dark d-flex align-items-center justify-content-between'>
                <h3 className='h5 mb-0'>
                  <i className='bi bi-trophy-fill me-2'></i>
                  Dashboard Titoli
                </h3>
                <button
                  type='button'
                  className='btn btn-outline-dark btn-sm ms-2'
                  onClick={() => setShowTitleDashboard(v => !v)}
                >
                  <i
                    className={`bi ${
                      showTitleDashboard ? 'bi-chevron-up' : 'bi-chevron-down'
                    }`}
                  ></i>
                  {showTitleDashboard ? ' Chiudi' : ' Apri'}
                </button>
              </div>
              {showTitleDashboard && (
                <div className='card-body'>
                  {/* Filtri titoli */}
                  <div className='mb-4 d-flex flex-column gap-2'>
                    <div className='d-flex flex-wrap gap-2'>
                      <div className='d-flex align-items-center gap-2'>
                        <span className='small text-uppercase text-muted fw-semibold'>
                          Tipo
                        </span>
                        <div className='btn-group btn-group-sm' role='group'>
                          <button
                            type='button'
                            className={`btn ${
                              titleFilter === 'all'
                                ? 'btn-dark'
                                : 'btn-outline-dark'
                            }`}
                            onClick={() => setTitleFilter('all')}
                          >
                            Tutti
                          </button>
                          <button
                            type='button'
                            className={`btn ${
                              titleFilter === 'singles'
                                ? 'btn-primary'
                                : 'btn-outline-primary'
                            }`}
                            onClick={() => setTitleFilter('singles')}
                          >
                            Singoli
                          </button>
                          <button
                            type='button'
                            className={`btn ${
                              titleFilter === 'tag_team'
                                ? 'btn-success'
                                : 'btn-outline-success'
                            }`}
                            onClick={() => setTitleFilter('tag_team')}
                          >
                            Team
                          </button>
                        </div>
                      </div>

                      <div className='d-flex align-items-center gap-2'>
                        <span className='small text-uppercase text-muted fw-semibold'>
                          Genere
                        </span>
                        <div className='btn-group btn-group-sm' role='group'>
                          <button
                            type='button'
                            className={`btn ${
                              titleGenderFilter === 'all'
                                ? 'btn-dark'
                                : 'btn-outline-dark'
                            }`}
                            onClick={() => setTitleGenderFilter('all')}
                          >
                            Tutti
                          </button>
                          <button
                            type='button'
                            className={`btn ${
                              titleGenderFilter === 'male'
                                ? 'btn-outline-info active'
                                : 'btn-outline-info'
                            }`}
                            onClick={() => setTitleGenderFilter('male')}
                          >
                            Maschili
                          </button>
                          <button
                            type='button'
                            className={`btn ${
                              titleGenderFilter === 'female'
                                ? 'btn-outline-danger active'
                                : 'btn-outline-danger'
                            }`}
                            onClick={() => setTitleGenderFilter('female')}
                          >
                            Femminili
                          </button>
                        </div>
                      </div>

                      <div className='d-flex align-items-center gap-2'>
                        <span className='small text-uppercase text-muted fw-semibold'>
                          Brand
                        </span>
                        <div className='btn-group btn-group-sm' role='group'>
                          <button
                            type='button'
                            className={`btn ${
                              titleUniverseFilter === 'all'
                                ? 'btn-dark'
                                : 'btn-outline-dark'
                            }`}
                            onClick={() => setTitleUniverseFilter('all')}
                          >
                            Tutti
                          </button>
                          <button
                            type='button'
                            className={`btn ${
                              titleUniverseFilter === 'wwe'
                                ? 'btn-outline-warning active'
                                : 'btn-outline-warning'
                            }`}
                            onClick={() => setTitleUniverseFilter('wwe')}
                          >
                            WWE
                          </button>
                          <button
                            type='button'
                            className={`btn ${
                              titleUniverseFilter === 'extra'
                                ? 'btn-outline-secondary active'
                                : 'btn-outline-secondary'
                            }`}
                            onClick={() => setTitleUniverseFilter('extra')}
                          >
                            Extra
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {titleDashboardCards.length === 0 ? (
                    <div className='alert alert-info'>
                      <i className='bi bi-info-circle me-2'></i>
                      Nessun titolo trovato con questo filtro.
                    </div>
                  ) : (
                    <>
                    <div className='d-none'>
                      {CHAMPIONSHIPS.filter(
                        championship => championship.value !== ''
                      )
                        .filter(
                          championship =>
                            titleFilter === 'all' ||
                            championship.type === titleFilter
                        )
                        .map(championship => {
                          const currentChampions =
                            findCurrentChampions(championship)
                          const requiredCount =
                            getRequiredWrestlersCount(championship)

                          return (
                            <div className='col-4' key={championship.value}>
                              <div
                                className={`card h-100 shadow-sm p-1 ${
                                  currentChampions.length === 0
                                    ? 'border-secondary'
                                    : 'border-warning'
                                }`}
                              >
                                <div className='card-body text-center d-flex flex-column'>
                                  {/* Contenitore per immagini sovrapposte */}
                                  <div
                                    className='position-relative mb-2'
                                    style={{ height: '80px' }}
                                  >
                                    {/* Immagine del titolo (sotto) */}
                                    <div
                                      className='position-absolute top-50 start-50 translate-middle'
                                      style={{ width: '100%', height: '100%' }}
                                    >
                                      {championship.type ===
                                      'mixed_tag_team' ? (
                                        // Per mixed tag team: mostra metà di ogni immagine
                                        <div className='position-relative w-100 h-100 d-flex'>
                                          {/* Metà sinistra - immagine maschile */}
                                          <div
                                            className='h-100'
                                            style={{
                                              width: '50%',
                                              overflow: 'hidden'
                                            }}
                                          >
                                            <img
                                              src={championship.imageMale}
                                              alt={`${championship.name} (Uomo)`}
                                              className='img-fluid opacity-50'
                                              style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                                objectPosition: 'right center'
                                              }}
                                              onError={e => {
                                                e.target.onerror = null
                                                e.target.src =
                                                  'https://via.placeholder.com/80x40?text=Male'
                                              }}
                                            />
                                          </div>

                                          {/* Metà destra - immagine femminile */}
                                          <div
                                            className='h-100'
                                            style={{
                                              width: '50%',
                                              overflow: 'hidden'
                                            }}
                                          >
                                            <img
                                              src={championship.imageFemale}
                                              alt={`${championship.name} (Donna)`}
                                              className='img-fluid opacity-50'
                                              style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                                objectPosition: 'left center'
                                              }}
                                              onError={e => {
                                                e.target.onerror = null
                                                e.target.src =
                                                  'https://via.placeholder.com/80x40?text=Female'
                                              }}
                                            />
                                          </div>
                                        </div>
                                      ) : championship.image ? (
                                        // Per altri titoli: immagine singola
                                        <img
                                          src={championship.image}
                                          alt={championship.name}
                                          className='img-fluid opacity-50'
                                          style={{
                                            maxHeight: '70px',
                                            objectFit: 'contain'
                                          }}
                                          onError={e => {
                                            e.target.onerror = null
                                            e.target.src =
                                              'https://via.placeholder.com/80x40?text=Titolo'
                                          }}
                                        />
                                      ) : (
                                        <div className='text-muted d-flex align-items-center justify-content-center h-100'>
                                          <i
                                            className='bi bi-trophy'
                                            style={{
                                              fontSize: '2.5rem',
                                              opacity: 0.3
                                            }}
                                          ></i>
                                        </div>
                                      )}
                                    </div>

                                    {/* Campioni correnti (sopra) */}
                                    {currentChampions.length > 0 ? (
                                      <div className='position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center gap-0'>
                                        {currentChampions.map(
                                          (champion, index) => (
                                            <div
                                              key={champion.id}
                                              className='position-relative'
                                            >
                                              <img
                                                src={champion.imageUrl}
                                                alt={champion.name}
                                                className='rounded-circle'
                                                style={{
                                                  width:
                                                    currentChampions.length ===
                                                    1
                                                      ? '100px'
                                                      : currentChampions.length ===
                                                        2
                                                      ? '70px'
                                                      : '50px',
                                                  height:
                                                    currentChampions.length ===
                                                    1
                                                      ? '100px'
                                                      : currentChampions.length ===
                                                        2
                                                      ? '70px'
                                                      : '50px',
                                                  objectFit: 'cover',
                                                  marginLeft:
                                                    index === 1 ? '-35px' : '0',
                                                  zIndex:
                                                    currentChampions.length -
                                                    index,
                                                  position:
                                                    index > 0
                                                      ? 'relative'
                                                      : 'static'
                                                }}
                                                onError={e => {
                                                  e.target.onerror = null
                                                  e.target.src =
                                                    'https://via.placeholder.com/60x60?text=W'
                                                }}
                                              />
                                            </div>
                                          )
                                        )}
                                      </div>
                                    ) : (
                                      /* Messaggio titolo vacante sopra l'immagine del titolo */
                                      <div className='position-absolute top-50 start-50 translate-middle'>
                                        <div
                                          className='bg-secondary text-white rounded px-2 py-1'
                                          style={{ fontSize: '0.7rem' }}
                                        >
                                          <i className='bi bi-exclamation-triangle me-1'></i>
                                          <strong>VACANT</strong>
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  {/* Nome del/i campione/i */}
                                  <div className='mb-1'>
                                    {currentChampions.length > 0 ? (
                                      <>
                                        {requiredCount > 1 ? (
                                          // Per titoli multipli: nomi su più righe
                                          <div className='d-flex flex-column gap-0'>
                                            {currentChampions.map(champion => (
                                              <div key={champion.id}>
                                                <div
                                                  className='fw-bold title-dashboard-champion-name'
                                                >
                                                  {champion.name}
                                                </div>
                                                {renderChampionTitleDays(
                                                  champion.id,
                                                  championship.value
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          // Per titoli singoli: nome centrale
                                          <>
                                            <div className='fw-bold title-dashboard-champion-name title-dashboard-champion-name--single'>
                                              {currentChampions[0].name}
                                            </div>
                                            {renderChampionTitleDays(
                                              currentChampions[0].id,
                                              championship.value
                                            )}
                                          </>
                                        )}
                                      </>
                                    ) : (
                                      // Nome del titolo per titoli vacanti
                                      <div
                                        className='fw-bold'
                                        style={{ fontSize: '0.8rem' }}
                                      >
                                        {championship.name}
                                      </div>
                                    )}
                                  </div>

                                  {/* Pulsanti azione */}
                                  <div className='mt-auto'>
                                    {currentChampions.length > 0 ? (
                                      <button
                                        className='btn btn-outline-danger btn-sm w-100'
                                        onClick={() =>
                                          handleRemoveTitle(championship)
                                        }
                                        title={`Rimuovi titolo`}
                                        style={{
                                          fontSize: '0.75rem',
                                          padding: '2px 5px'
                                        }}
                                      >
                                        <i className='bi bi-x-circle me-1'></i>
                                        Rimuovi
                                      </button>
                                    ) : (
                                      <button
                                        className='btn btn-outline-success btn-sm w-100'
                                        onClick={() =>
                                          openTitleAssignmentModal(championship)
                                        }
                                        title={`Assegna ${championship.name}`}
                                        style={{
                                          fontSize: '0.75rem',
                                          padding: '2px 5px'
                                        }}
                                      >
                                        <i className='bi bi-plus-circle me-1'></i>
                                        Assegna
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                    </div>
                    <div className='row row-cols-2 row-cols-md-3 row-cols-xl-4 g-2'>
                      {titleDashboardCards.map(renderTitleDashboardCard)}
                    </div>
                    </>
                  )}
                </div>
              )}
            </div>

{showTitleRemovalModal && titleRemovalTitle && (
  <div
    className='modal fade show d-block'
    tabIndex='-1'
    style={{
      backgroundColor: 'rgba(0,0,0,0.82)',
      backdropFilter: 'blur(6px)'
    }}
  >
    <div className='modal-dialog modal-dialog-centered modal-lg'>
      <div className='modal-content bg-dark text-light border-0 shadow-lg rounded-4 overflow-hidden'>
        <div className='modal-header bg-gradient-dark border-secondary pb-2'>
          <div>
            <h5 className='modal-title fw-bold'>
              <i className='bi bi-x-circle text-danger me-2'></i>
              Scegli chi rimuovere
            </h5>
            <small className='text-muted'>
              {titleRemovalTitle.name}
            </small>
          </div>
          <button
            type='button'
            className='btn-close btn-close-white'
            onClick={closeTitleRemovalModal}
            disabled={Boolean(titleRemovalSubmittingId)}
          />
        </div>

        <div className='modal-body bg-dark p-4'>
          <div className='title-modal-current mb-4'>
            <div className='title-modal-current__eyebrow'>
              Titolo condiviso
            </div>
            <div className='title-modal-current__names'>
              Scegli il detentore da rimuovere
            </div>
            <small className='text-muted'>
              Per i titoli singoli con due detentori puoi chiudere solo il regno desiderato, incluso quello interim.
            </small>
          </div>

          <div className='title-removal-grid'>
            {titleRemovalChampions.map(champion => {
              const isInterimChampion = isInterimChampionForTitle(
                champion,
                titleRemovalTitle.value
              )
              const isSubmitting = titleRemovalSubmittingId === champion.id

              return (
                <button
                  key={champion.id}
                  type='button'
                  className={`title-removal-option ${
                    isInterimChampion ? 'title-removal-option--interim' : ''
                  }`}
                  onClick={() => handleRemoveTitle(titleRemovalTitle, [champion])}
                  disabled={Boolean(titleRemovalSubmittingId)}
                >
                  <img
                    src={champion.imageUrl}
                    alt={champion.name}
                    className={`rounded-circle border border-2 title-removal-option__avatar ${
                      isInterimChampion
                        ? 'title-removal-option__avatar--interim'
                        : 'title-removal-option__avatar--official'
                    }`}
                    onError={e => {
                      e.target.onerror = null
                      e.target.src = 'https://via.placeholder.com/80x80?text=W'
                    }}
                  />

                  <span className='title-removal-option__body'>
                    <strong>{champion.name}</strong>
                    <span className='title-removal-option__meta'>
                      {isInterimChampion
                        ? 'Campione Interim'
                        : 'Campione ufficiale'}
                    </span>
                    {renderChampionTitleDays(champion.id, titleRemovalTitle.value)}
                  </span>

                  <span className='title-removal-option__cta'>
                    {isSubmitting ? 'Rimozione...' : 'Rimuovi'}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        <div className='modal-footer border-secondary bg-dark d-flex justify-content-end'>
          <button
            type='button'
            className='btn btn-outline-secondary'
            onClick={closeTitleRemovalModal}
            disabled={Boolean(titleRemovalSubmittingId)}
          >
            Annulla
          </button>
        </div>
      </div>
    </div>
  </div>
)}

{/* MODAL PER ASSEGNAZIONE TITOLO - versione grafica */}
{showTitleModal && selectedTitle && (
  <div
    className='modal fade show d-block'
    tabIndex='-1'
    style={{
      backgroundColor: 'rgba(0,0,0,0.85)',
      backdropFilter: 'blur(6px)'
    }}
  >
    <div className='modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl'>
      <div className='modal-content bg-dark text-light border-0 shadow-lg rounded-4 overflow-hidden'>
        {/* Header con immagine del titolo */}
        <div className='modal-header bg-gradient-dark border-secondary pb-2'>
          <div className='d-flex align-items-center gap-3'>
            {selectedTitle.image && (
              <img
                src={selectedTitle.image}
                alt={selectedTitle.name}
                style={{ height: '48px', width: 'auto', objectFit: 'contain' }}
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
            <div>
              <h5 className='modal-title fw-bold'>
                <i className='bi bi-trophy-fill text-warning me-2'></i>
                {titleModalMode === 'replace' ? 'Cambia detentore' : 'Assegna'}: {selectedTitle.name}
              </h5>
              <small className='text-muted'>
                {selectedTitle.type === 'mixed_tag_team' && 'Composizione: 1 uomo + 1 donna'}
                {selectedTitle.type === 'trios' && 'Composizione: 3 uomini'}
                {selectedTitle.type === 'tag_team' && `Composizione: 2 ${selectedTitle.gender === 'female' ? 'donne' : 'uomini'}`}
                {selectedTitle.type === 'singles' && `Composizione: 1 ${selectedTitle.gender === 'female' ? 'donna' : 'uomo'}`}
              </small>
            </div>
          </div>
          <button
            type='button'
            className='btn-close btn-close-white'
            onClick={closeTitleModal}
          />
        </div>

        <div className='modal-body bg-dark p-4'>
          {titleModalMode === 'replace' && currentTitleChampions.length > 0 && (
            <div className='title-modal-current mb-4'>
              <div className='title-modal-current__eyebrow'>
                Detentore attuale
              </div>
              <div className='title-modal-current__names'>
                {currentTitleChampions.map(champion => champion.name).join(', ')}
              </div>
              {currentTitleIsInterim && (
                <span className='title-modal-current__badge'>
                  <i className='bi bi-hourglass-split me-1' aria-hidden='true'></i>
                  Regno provvisorio attivo
                </span>
          )}
            </div>
          )}

          {titleModalMode === 'replace' &&
            !assignInterimChampion &&
            currentTitleChangePenaltySuggestions.length > 0 && (
              <div className='title-modal-penalties mb-4'>
                <div className='title-modal-penalties__eyebrow'>
                  Bonus suggeriti per il campione uscente
                </div>
                <div className='title-modal-penalties__list'>
                  {currentTitleChangePenaltySuggestions.map(suggestion => (
                    <div
                      key={suggestion.id}
                      className='title-modal-penalty-card'
                    >
                      <strong>{suggestion.wrestlerName}</strong>
                      <span>{suggestion.bonusLabel}</span>
                      <small>
                        {suggestion.daysAsChampion} giorni da campione.
                        {' '}
                        Show suggerito:{' '}
                        {formatSuggestedShowLabel(suggestion.suggestedShow)}
                      </small>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {titleModalMode === 'replace' && (
            <div className='title-modal-advanced mb-4'>
              <details>
                <summary>Opzioni avanzate</summary>
                <div className='form-check mt-3 mb-1'>
                  <input
                    id='interimChampionToggle'
                    type='checkbox'
                    className='form-check-input'
                    checked={assignInterimChampion}
                    onChange={event =>
                      setAssignInterimChampion(event.target.checked)
                    }
                  />
                  <label
                    className='form-check-label'
                    htmlFor='interimChampionToggle'
                  >
                    Segna il nuovo detentore come campione provvisorio
                  </label>
                </div>
                <small className='text-muted'>
                  Opzione rara: usala solo quando il titolo resta temporaneo.
                </small>
              </details>
            </div>
          )}

          {titleTeamSuggestions.length > 0 && (
            <div className='title-team-suggestions mb-4'>
              <div className='title-team-suggestions__header'>
                <div>
                  <strong>
                    <i className='bi bi-people-fill me-2'></i>
                    Team suggeriti
                  </strong>
                  <small>
                    I team compatibili compaiono qui per velocizzare il cambio campione.
                  </small>
                </div>
                {activeTitleTeamSuggestion ? (
                  <span className='title-team-suggestions__active'>
                    Attivo: {activeTitleTeamSuggestion.name}
                  </span>
                ) : null}
              </div>

              <div className='title-team-suggestions__grid'>
                {titleTeamSuggestions.map(teamSuggestion => (
                  <button
                    key={teamSuggestion.id}
                    type='button'
                    className={`title-team-suggestion-card ${
                      activeTitleTeamSuggestionId === teamSuggestion.id
                        ? 'is-active'
                        : ''
                    }`}
                    onClick={() =>
                      handleTitleTeamSuggestionSelect(teamSuggestion)
                    }
                  >
                    <span className='title-team-suggestion-card__top'>
                      <strong>{teamSuggestion.name}</strong>
                      <span className='title-team-suggestion-card__pill'>
                        {teamSuggestion.requiresManualChoice
                          ? 'Scegli tu'
                          : 'Pronto'}
                      </span>
                    </span>
                    <span className='title-team-suggestion-card__members'>
                      {teamSuggestion.compatibleMemberNames.join(', ')}
                    </span>
                    <small>
                      {teamSuggestion.requiresManualChoice
                        ? `Ci sono ${teamSuggestion.compatibleMembers.length} membri compatibili: dopo il click scegli quelli giusti.`
                        : 'Selezione automatica disponibile con questo blocco.'}
                    </small>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Area di selezione corrente - stile "slot" */}
          <div className='mb-4'>
            <h6 className='mb-3'>
              <i className='bi bi-person-plus-fill text-success me-2'></i>
              Selezionati ({selectedWrestlers.length}/{getRequiredWrestlersCount(selectedTitle)})
              {selectedTitle.type === 'mixed_tag_team' && (
                <span className='ms-2 small text-info'>
                  (U: {selectedWrestlers.filter(w => w.gender === 'Uomo').length} / D: {selectedWrestlers.filter(w => w.gender === 'Donna').length})
                </span>
              )}
            </h6>
            <div className='d-flex flex-wrap gap-3 align-items-center'>
              {selectedWrestlers.length === 0 && (
                <div className='text-muted fst-italic'>Nessun wrestler selezionato</div>
              )}
              {selectedWrestlers.map(wrestler => (
                <div
                  key={wrestler.id}
                  className='d-flex align-items-center gap-2 bg-success bg-opacity-25 rounded-4 p-2 pe-3 border border-success'
                >
                  <img
                    src={wrestler.imageUrl}
                    alt={wrestler.name}
                    className='rounded-circle'
                    style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                    onError={(e) => e.target.src = 'https://via.placeholder.com/40'}
                  />
                  <span className='fw-semibold'>{wrestler.name}</span>
                  <button
                    type='button'
                    className='btn btn-sm btn-link text-white p-0 ms-1'
                    onClick={() => toggleWrestlerSelection(wrestler)}
                  >
                    <i className='bi bi-x-circle-fill text-danger'></i>
                  </button>
                </div>
              ))}
              {/* Slot vuoti per completare il team */}
              {Array.from({ length: getRequiredWrestlersCount(selectedTitle) - selectedWrestlers.length }).map((_, idx) => (
                <div
                  key={`empty-${idx}`}
                  className='d-flex align-items-center justify-content-center bg-secondary bg-opacity-25 rounded-4'
                  style={{ width: 'auto', minWidth: '100px', height: '52px', padding: '0 12px', border: '1px dashed #6c757d' }}
                >
                  <i className='bi bi-plus-circle text-muted me-1'></i>
                  <span className='small text-muted'>vuoto</span>
                </div>
              ))}
            </div>
          </div>

          {/* Ricerca */}
          <div className='mb-3'>
            <div className='input-group'>
              <span className='input-group-text bg-transparent border-secondary text-muted'>
                <i className='bi bi-search'></i>
              </span>
              <input
                type='text'
                className='form-control bg-dark text-light border-secondary'
                placeholder='Cerca wrestler per nome...'
                value={titleAssignmentSearch}
                onChange={e => setTitleAssignmentSearch(e.target.value)}
                autoFocus
              />
              {titleAssignmentSearch && (
                <button
                  className='btn btn-outline-secondary'
                  type='button'
                  onClick={() => setTitleAssignmentSearch('')}
                >
                  <i className='bi bi-x'></i>
                </button>
              )}
            </div>
          </div>

          {/* Griglia wrestler con anteprime immagini */}
          <div
            className='row g-3'
            style={{ maxHeight: '400px', overflowY: 'auto' }}
          >
            {getEnhancedFilteredWrestlersForTitle().map(wrestler => {
              const isSelected = isWrestlerSelected(wrestler.id)
              const selectionLimitReached = selectedWrestlers.length >= getRequiredWrestlersCount(selectedTitle) && !isSelected
              const isSuggestedTeamMember =
                activeTitleTeamSuggestion &&
                activeTitleTeamSuggestion.compatibleMemberIds.includes(
                  wrestler.id
                )
              const genderMismatch = (() => {
                if (selectedTitle.type === 'mixed_tag_team') {
                  const maleCount = selectedWrestlers.filter(w => w.gender === 'Uomo').length
                  const femaleCount = selectedWrestlers.filter(w => w.gender === 'Donna').length
                  if (wrestler.gender === 'Uomo' && maleCount >= 1) return true
                  if (wrestler.gender === 'Donna' && femaleCount >= 1) return true
                }
                if (selectedTitle.type === 'trios' && wrestler.gender !== 'Uomo') return true
                if (selectedTitle.type === 'tag_team' && selectedTitle.gender === 'female' && wrestler.gender !== 'Donna') return true
                if (selectedTitle.type === 'tag_team' && selectedTitle.gender === 'male' && wrestler.gender !== 'Uomo') return true
                if (selectedTitle.type === 'singles' && selectedTitle.gender === 'female' && wrestler.gender !== 'Donna') return true
                if (selectedTitle.type === 'singles' && selectedTitle.gender === 'male' && wrestler.gender !== 'Uomo') return true
                return false
              })()

              let disabled = selectionLimitReached || genderMismatch
              let disabledTitle = ''
              if (selectionLimitReached) disabledTitle = `Hai già selezionato ${selectedWrestlers.length} wrestler su ${getRequiredWrestlersCount(selectedTitle)}`
              if (genderMismatch) disabledTitle = 'Genere non compatibile con questo titolo'

              return (
                <div className='col-6 col-md-4 col-lg-3' key={wrestler.id}>
                  <button
                  type='button'
                  className={`card h-100 bg-dark border-${isSelected ? 'success' : 'secondary'} ${disabled ? 'opacity-50' : 'hover-grow'} transition-all w-100 text-start p-0`}
                    style={{
                      background: isSelected
                        ? 'rgba(40, 167, 69, 0.1)'
                        : isSuggestedTeamMember
                        ? 'rgba(255, 193, 7, 0.08)'
                        : 'transparent',
                      cursor: disabled ? 'not-allowed' : 'pointer'
                    }}
                    onClick={() => !disabled && toggleWrestlerSelection(wrestler)}
                    disabled={disabled}
                    title={disabledTitle}
                  >
                    <div className='card-body d-flex flex-column align-items-center text-center p-3'>
                      <div className='position-relative mb-2'>
                        <img
                          src={wrestler.imageUrl}
                          alt={wrestler.name}
                          className='rounded-circle border border-2'
                          style={{ width: '80px', height: '80px', objectFit: 'cover', borderColor: isSelected ? '#28a745' : '#6c757d' }}
                          onError={(e) => e.target.src = 'https://via.placeholder.com/80'}
                        />
                        {isSelected && (
                          <div className='position-absolute top-0 end-0 bg-success rounded-circle p-1'>
                            <i className='bi bi-check-lg text-white' style={{ fontSize: '0.8rem' }}></i>
                          </div>
                        )}
                      </div>
                      <h6 className='mb-1 text-truncate w-100'>{wrestler.name}</h6>
                      <div className='d-flex gap-2 justify-content-center small text-muted'>
                        <span>
                          {wrestler.gender === 'Uomo' ? <i className='bi bi-gender-male text-primary'></i> : <i className='bi bi-gender-female text-danger'></i>}
                        </span>
                        {isSuggestedTeamMember && (
                          <span className='title-team-member-hint'>
                            Team
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                </div>
              )
            })}

            {getEnhancedFilteredWrestlersForTitle().length === 0 && (
              <div className='col-12 text-center py-5'>
                <i className='bi bi-emoji-frown display-3 text-muted'></i>
                <p className='text-muted mt-2'>Nessun wrestler trovato</p>
              </div>
            )}
          </div>
        </div>

        <div className='modal-footer border-secondary bg-dark d-flex justify-content-between'>
          <div className='text-muted small'>
            <i className='bi bi-info-circle me-1'></i>
            Clicca su un wrestler per selezionarlo/deselezionarlo
          </div>
          <div>
            <button
              type='button'
              className='btn btn-outline-secondary me-2'
              onClick={closeTitleModal}
            >
              Annulla
            </button>
            <button
              type='button'
              className='btn btn-warning'
              onClick={handleAssignTitle}
              disabled={selectedWrestlers.length !== getRequiredWrestlersCount(selectedTitle)}
            >
              <i className='bi bi-check-circle me-1'></i>
              {titleModalMode === 'replace' ? 'Conferma cambio' : 'Assegna titolo'} ({selectedWrestlers.length}/{getRequiredWrestlersCount(selectedTitle)})
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
)}

            <div className='card shadow-sm mb-4 wrestler-editor-card'>
              <div className='card-header bg-primary text-white d-flex align-items-center justify-content-between gap-3 flex-wrap'>
                <div>
                  <h2 className='h5 mb-1'>
                    <i className='bi bi-plus-circle me-2'></i>
                    Aggiungi wrestler
                  </h2>
                </div>
                <button
                  type='button'
                  className='btn btn-outline-light btn-sm wrestler-editor-card__toggle'
                  onClick={handleToggleCreateForm}
                >
                  <i
                    className={`bi ${
                      showCreateForm ? 'bi-dash-circle' : 'bi-plus-circle'
                    }`}
                  ></i>
                  {showCreateForm ? ' Chiudi' : ' Apri'}
                </button>
              </div>

              {showCreateForm && (
                <div className='card-body p-3 p-md-4'>
                  {renderWrestlerForm({ mode: 'create' })}
                </div>
              )}
            </div>

            <div className='card shadow-sm'>
              <div className='card-header bg-light d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 gap-md-0 rounded-top shadow-sm'>
                {/* Titolo a sinistra */}
                <h3 className='h5 mb-0'>
                  Lista Wrestler ({filteredWrestlers.length}/{wrestlers.length})
                </h3>

                {/* Gruppo ricerca + filtro, allineato a destra su desktop */}
                <div className='d-flex flex-column flex-sm-row gap-2 w-100 w-md-auto ms-md-auto'>
                  <div className='flex-grow-1'>
                    <input
                      type='text'
                      className='form-control form-control-sm'
                      placeholder='Cerca wrestler...'
                      aria-label='Cerca wrestler'
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div>
                    <select
                      className='form-select form-select-sm'
                      value={activeFilter}
                      onChange={e => setActiveFilter(e.target.value)}
                      aria-label='Filtro attivazione'
                    >
                      <option value='all'>Tutti</option>
                      <option value='active'>Solo attivi</option>
                      <option value='inactive'>Solo inattivi</option>
                      <option value='guests'>Solo ospiti</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className='card-body p-0'>
                {wrestlers.length === 0 ? (
                  <div className='alert alert-warning m-4'>
                    <i className='bi bi-info-circle me-2'></i>
                    Nessun wrestler registrato. Aggiungi il primo wrestler!
                  </div>
                ) : (
                  <div className='table-responsive'>
                    <table className='table mb-0'>
                      <thead className='table-light'>
                        <tr>
                          <th
                            style={{ width: '25%', cursor: 'pointer' }}
                            onClick={() => handleSort('name')}
                          >
                            Wrestler{' '}
                            {sortBy === 'name' ? (
                              sortOrder === 'asc' ? (
                                <i className='bi bi-caret-up-fill'></i>
                              ) : (
                                <i className='bi bi-caret-down-fill'></i>
                              )
                            ) : (
                              <i className='bi bi-filter'></i>
                            )}
                          </th>
                          <th
                            className='text-center'
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleSort('roster')}
                          >
                            Roster{' '}
                            {sortBy === 'roster' ? (
                              sortOrder === 'asc' ? (
                                <i className='bi bi-caret-up-fill'></i>
                              ) : (
                                <i className='bi bi-caret-down-fill'></i>
                              )
                            ) : null}
                          </th>
                          <th
                            className='text-center'
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleSort('status')}
                          >
                            Status{' '}
                            {sortBy === 'status' ? (
                              sortOrder === 'asc' ? (
                                <i className='bi bi-caret-up-fill'></i>
                              ) : (
                                <i className='bi bi-caret-down-fill'></i>
                              )
                            ) : null}
                          </th>
                          <th
                            className='text-center'
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleSort('alignment')}
                          >
                            Allineamento{' '}
                            {sortBy === 'alignment' ? (
                              sortOrder === 'asc' ? (
                                <i className='bi bi-caret-up-fill'></i>
                              ) : (
                                <i className='bi bi-caret-down-fill'></i>
                              )
                            ) : null}
                          </th>
                          <th
                            className='text-center'
                            style={{ width: '120px' }}
                          >
                            Azioni
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredWrestlers.length === 0 ? (
                          <tr>
                            <td colSpan='5' className='text-center py-4'>
                              <div className='alert alert-warning mb-0'>
                                <i className='bi bi-exclamation-triangle me-2'></i>
                                Nessun wrestler trovato con questo nome.
                              </div>
                            </td>
                          </tr>
                        ) : (
                          displayedWrestlers.map(wrestler => (
                            <React.Fragment key={wrestler.id}>
                              <tr
                                className={`${
                                  editingId === wrestler.id
                                    ? 'table-active'
                                    : ''
                                } ${
                                  wrestler.active === false
                                    ? 'text-brand-muted bg-light'
                                    : ''
                                }`}
                              >
                                <td>
                                  <div className='d-flex align-items-center'>
                                    <img
                                      src={wrestler.imageUrl}
                                      alt={wrestler.name}
                                      className='rounded-circle me-3 bg-secondary'
                                      style={{
                                        width: '40px',
                                        height: '40px',
                                        objectFit: 'cover'
                                      }}
                                    />
                                    <Link
                                      to={`/wrestlers/${wrestler.id}`}
                                      className='text-decoration-none text-light'
                                    >
                                      <span className='fw-bold me-2'>
                                        {wrestler.name}
                                      </span>
                                    </Link>
                                    {wrestler.isGuest && (
                                      <span
                                        className='badge bg-secondary me-2'
                                        title='Ospite: dati minimi, convertibile in wrestler fisso'
                                      >
                                        <i className='bi bi-person-badge-fill me-1'></i>
                                        Ospite
                                      </span>
                                    )}
                                    {wrestler.premiumComment && (
                                      <i
                                        className='bi bi-stars text-brand-secondary me-2'
                                        title='Commento Premium presente'
                                      ></i>
                                    )}
                                    {wrestler.gender === 'Uomo' ? (
                                      <i
                                        className='bi bi-gender-male text-primary'
                                        title='Uomo'
                                      ></i>
                                    ) : wrestler.gender === 'Donna' ? (
                                      <i
                                        className='bi bi-gender-female text-danger'
                                        title='Donna'
                                      ></i>
                                    ) : (
                                      <i
                                        className='bi bi-gender-ambiguous text-secondary'
                                        title={wrestler.gender || 'N/A'}
                                      ></i>
                                    )}
                                  </div>
                                </td>

                                <td className='text-center fs-5'>
                                  <span
                                    className={`${getBrandBadgeClass(
                                      wrestler.brand
                                    )} badge px-2 py-1`}
                                  >
                                    {wrestler.brand === 'Free Agent'
                                      ? 'FREE'
                                      : wrestler.brand === 'Raw'
                                      ? 'RAW'
                                      : wrestler.brand === 'SmackDown'
                                      ? 'SD'
                                      : 'NXT'}
                                  </span>
                                </td>

                                <td className='text-center fs-4'>
                                  {wrestler.isInjured ? (
                                    <InjuryIcon />
                                  ) : (
                                    <span>
                                      <i className='bi bi-check-circle text-success'></i>
                                    </span>
                                  )}
                                </td>
                                <td className='text-center fs-4'>
                                  {wrestler.alignment === 'face' ? (
                                    <i
                                      className='bi bi-emoji-smile text-success'
                                      title='Face'
                                    ></i>
                                  ) : wrestler.alignment === 'heel' ? (
                                    <i
                                      className='bi bi-emoji-angry-fill text-danger'
                                      title='Heel'
                                    ></i>
                                  ) : (
                                    <span className='badge bg-secondary'>
                                      N/A
                                    </span>
                                  )}
                                </td>

                                <td>
                                  <div className='d-flex justify-content-center gap-1'>
                                    {editingId === wrestler.id ? (
                                      <>
                                        <button
                                          className='btn btn-sm btn-success'
                                          onClick={handleSubmit}
                                          title='Conferma'
                                        >
                                          <i className='bi bi-check'></i>
                                        </button>
                                        <button
                                          className='btn btn-sm btn-outline-danger'
                                          onClick={resetForm}
                                          title='Annulla'
                                        >
                                          <i className='bi bi-x'></i>
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          className='btn btn-sm btn-outline-primary'
                                          onClick={() => handleEdit(wrestler)}
                                          title='Modifica'
                                        >
                                          <i className='bi bi-pencil'></i>
                                        </button>
                                        <button
                                          className='btn btn-sm btn-outline-danger'
                                          onClick={() =>
                                            handleDelete(wrestler.id)
                                          }
                                          title='Elimina'
                                        >
                                          <i className='bi bi-trash'></i>
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {expandedEditId === wrestler.id && (
                                <tr>
                                  <td colSpan='5' className='p-0'>
                                    <div className='edit-inline-form wrestler-inline-editor border-top'>
                                      <div className='wrestler-inline-editor__header'>
                                      </div>
                                      {renderWrestlerForm({
                                        mode: 'edit',
                                        wrestler
                                      })}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            <div className='card shadow-sm mt-3'>
              <div className='card-body'>
                <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3'>
                  <div>
                    <h5 className='mb-1'>
                      <i className='bi bi-archive-fill me-2 text-warning'></i>
                      Archivio Punteggi Stagionali
                    </h5>
                  </div>
                  <button
                    type='button'
                    className='btn btn-warning'
                    onClick={handleArchiveAllCurrentScores}
                    disabled={
                      bulkArchiveRunning ||
                      bulkResetRunning ||
                      wrestlers.length === 0
                    }
                  >
                    {bulkArchiveRunning ? (
                      <>
                        <span className='spinner-border spinner-border-sm me-2' />
                        Archiviazione...
                      </>
                    ) : (
                      <>
                        <i className='bi bi-fast-forward-btn me-2'></i>
                        Archivia tutti i punteggi
                      </>
                    )}
                  </button>
                </div>

                <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mt-3 pt-3 border-top'>
                  <div>
                    <h6 className='mb-1'>
                      <i className='bi bi-arrow-counterclockwise me-2 text-danger'></i>
                      Reset punteggi correnti
                    </h6>
                  </div>
                  <button
                    type='button'
                    className='btn btn-outline-danger'
                    onClick={handleResetAllCurrentScores}
                    disabled={
                      bulkResetRunning ||
                      bulkArchiveRunning ||
                      wrestlers.length === 0
                    }
                  >
                    {bulkResetRunning ? (
                      <>
                        <span className='spinner-border spinner-border-sm me-2' />
                        Reset in corso...
                      </>
                    ) : (
                      <>
                        <i className='bi bi-eraser me-2'></i>
                        Resetta tutti i punteggi
                      </>
                    )}
                  </button>
                </div>

                {(bulkArchiveRunning ||
                  bulkResetRunning ||
                  bulkArchiveProgress.total > 0) && (
                  <div className='mt-3'>
                    <div className='d-flex justify-content-between small mb-1'>
                      <span>Progresso</span>
                      <span>
                        {bulkArchiveProgress.current} /{' '}
                        {bulkArchiveProgress.total}
                      </span>
                    </div>
                    <div className='progress' style={{ height: '10px' }}>
                      <div
                        className={`progress-bar ${
                          bulkArchiveRunning
                            ? 'progress-bar-striped progress-bar-animated bg-warning text-dark'
                            : 'bg-success'
                        }`}
                        role='progressbar'
                        style={{
                          width: `${
                            bulkArchiveProgress.total
                              ? (bulkArchiveProgress.current /
                                  bulkArchiveProgress.total) *
                                100
                              : 0
                          }%`
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProtectedRoute>
  )
}

export default AddWrestler
